import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Loader2, CheckCircle, ChevronDown, ArrowRight, RotateCcw } from "lucide-react";
import hospitalService from "../services/hospitalService";
import bookingService from "../services/bookingService";
import authService from "../services/authService";
import apiService from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";
import { API_CONFIG } from "../config/api";
import { getAmbulanceTypeAbbreviation } from "../utils/ambulanceTypeHelper";
import ManualAllocationModal from "./ambulance/ManualAllocationModal";
import AutomaticAllocationModal from "./ambulance/AutomaticAllocationModal";
import ErrorModal from "./ui/ErrorModal";
import ambulanceImage1 from "../svg/Rectangle.png";
import ambulanceImage2 from "../svg/Rectangle-2.png";
import ambulanceImage3 from "../svg/Rectangle-3.png";
import ambulanceImage4 from "../svg/Rectangle-4.png";
import ambulanceImage5 from "../svg/Rectangle-5.png";
import ambulanceImage6 from "../svg/Rectangle-6.png";

// CSS for range slider
const sliderStyles = `
  .slider::-webkit-slider-thumb {
    appearance: none;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  .slider::-moz-range-thumb {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  .slider:focus {
    outline: none;
  }
  
  .slider:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = sliderStyles;
  document.head.appendChild(styleSheet);
}

// Array of ambulance images
const ambulanceImages = [ambulanceImage1, ambulanceImage2, ambulanceImage3, ambulanceImage4, ambulanceImage5, ambulanceImage6];

  // Function to calculate distance between two coordinates using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance in kilometers
  return distance;
};

// Helper function to handle ambulance type checkbox changes
const handleAmbulanceTypeChange = (typeId, isChecked, setSelectedAmbulanceTypes) => {
  setSelectedAmbulanceTypes(prev => {
    if (isChecked) {
      // Add the type if it's not already selected
      return prev.includes(typeId) ? prev : [...prev, typeId];
    } else {
      // Remove the type if it's currently selected
      return prev.filter(id => id !== typeId);
    }
  });
};

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Dropdown button component for payment selection
const PaymentDropdownButton = ({ value, label, icon, currentPaymentType, onSelect, onClose }) => (
  <button
    type="button"
    className={`w-full px-4 py-3 text-sm text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between ${
      currentPaymentType === value
        ? "bg-blue-50 text-blue-700"
        : "text-gray-700"
    }`}
    onClick={() => {
      onSelect(value);
      onClose();
    }}
  >
    <span className="flex items-center gap-2">{icon} {label}</span>
    {currentPaymentType === value && (
      <CheckCircle className="w-4 h-4 text-blue-600" />
    )}
  </button>
);

// Helper function to check if booking button should be disabled
const isBookingDisabled = (isLoading, bookingFormData, selectedAmbulance, pickupCoordinates, dropCoordinates, allocationType, ambulanceTypes = []) => {
  if (!bookingFormData) {
    console.log('🚫 Booking disabled: No bookingFormData');
    return true;
  }
  
  // Core requirements for booking
  const coreConditions = {
    isLoading,
    hasAllocationType: !!allocationType
  };

  // For manual allocation, ambulance selection is required
  // For automatic allocation, ambulance selection is optional if filtered ambulances are available
  const ambulanceRequirement = allocationType === "Manual" 
    ? !!selectedAmbulance 
    : !!(selectedAmbulance || ambulanceTypes.length > 0);
  
  // Optional conditions that might be missing
  const optionalConditions = {
    hasName: bookingFormData.name?.trim(),
    hasContact: bookingFormData.phone?.trim() || bookingFormData.email?.trim(),
    hasPickup: !!pickupCoordinates,
    hasDrop: !!dropCoordinates,
    hasScheduleData: bookingFormData.bookingType !== "scheduled" || (bookingFormData.date && bookingFormData.time),
    hasAmbulanceOrTypes: ambulanceRequirement
  };
  
  console.log('🔍 Booking button conditions:', { ...coreConditions, ...optionalConditions });
  console.log('📋 BookingFormData keys:', Object.keys(bookingFormData));
  console.log('� SeleoctedAmbulance:', selectedAmbulance?.name || 'none');
  console.log('⚙️ AllocationType:', allocationType || 'none');
  console.log('🚑 Available ambulance types:', ambulanceTypes.length);
  
  // Require allocation type and ambulance (or available types for automatic)
  const isDisabled = coreConditions.isLoading || !coreConditions.hasAllocationType || !optionalConditions.hasAmbulanceOrTypes;
    
  console.log('🚫 Button disabled:', isDisabled, 'Reason:', 
    coreConditions.isLoading ? 'Loading' : 
    !coreConditions.hasAllocationType ? 'No allocation type selected' : 
    !optionalConditions.hasAmbulanceOrTypes ? 'No ambulance available' : 'Should be enabled');
  
  return isDisabled;
};

const ChooseAmbulanceDuplicate = ({
  onAmbulanceSelect,
  selectedAmbulance,
  onConfirmSelection,
  pickupCoordinates,
  dropCoordinates,
  onFareEstimatesChange,
  onVendorNamesChange, // Callback to pass vendor names to parent
  journeyDirection,
  bookingFormData,
  triggerBooking,
  onAllocationTypeChange,
  onAmbulanceTypesChange,
  onStartAutoAssignment, // Callback to notify parent to start auto-assignment
  onOpenManualAllocationModal, // Callback to expose modal opening function to parent
  onManualSearch, // Callback to trigger candidates API call for manual search
  allocationStatus, // Allocation status from poll API
  bookingId, // Current booking ID
  isPollingAllocation, // Whether polling is active
  onFiltersChange, // Callback to notify parent of filter changes
  onPaymentTypeChange, // Callback to notify parent of payment type changes
  onStopAllocation, // Callback to stop allocation process when cancel is clicked
  onRefetchCandidates, // Callback from parent to refetch candidates with bookingId (for fleet users after stop allocation)
  onRefetchAllocationStatus, // Callback to refetch allocation status (for WebSocket integration)
}) => {
  const { user } = useAuth();
  
  // Store parent's refetch function to avoid naming conflict with local function
  const parentRefetchFn = onRefetchCandidates;

  // Helper: Check if user is a hospital user
  const isHospitalUser = useMemo(() => {
    return user?.role === 'HOSPITAL_USER' ||
      user?.role === 'hospital' ||
      user?.role === 'Hospital';
  }, [user?.role]);

  // Helper: Check if user is a fleet user
  const isFleetUser = useMemo(() => {
    return user?.role === 'TENANT_ADMIN' ||
      user?.role === 'tenant_admin' ||
      user?.role === 'Fleet';
  }, [user?.role]);
  
  const [ambulanceTypes, setAmbulanceTypes] = useState([]);
  const [allAmbulanceTypes, setAllAmbulanceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fareEstimates, setFareEstimates] = useState({});
  const [fareLoading, setFareLoading] = useState({});
  const [estimateDistanceKm, setEstimateDistanceKm] = useState(null);
  const [estimateDurationMinutes, setEstimateDurationMinutes] = useState(null);
  const [isFiltered, setIsFiltered] = useState(false);
  const [filterInfo, setFilterInfo] = useState(null);
  const [fleetPartnerNames, setFleetPartnerNames] = useState([]);
  const [fleetOperatorMap, setFleetOperatorMap] = useState({});
  const [partnerTypeIds, setPartnerTypeIds] = useState(new Set());
  const [pricingInfoData, setPricingInfoData] = useState(null);
  const [vendorNamesMap, setVendorNamesMap] = useState({});

  // Filter states - Select Partner, KM Range, and Ambulance Types
  const [selectedPartner, setSelectedPartner] = useState("all_partners"); // Partner filter - default to "all_partners" for hospital dashboard instant booking
  const [kmRange, setKmRange] = useState(50); // Default to 50km range
  const [selectedAmbulanceTypes, setSelectedAmbulanceTypes] = useState([]); // Array of selected type IDs - starts empty, user must manually select

  // Partner dropdown state
  const [isPartnerDropdownOpen, setIsPartnerDropdownOpen] = useState(false);
  const partnerDropdownRef = useRef(null);

  // Available partners state - from estimate fare API response
  const [availablePartners, setAvailablePartners] = useState([]);
  const [partnersFromEstimateFare, setPartnersFromEstimateFare] = useState([]);

  // Payment state
  const [paymentType, setPaymentType] = useState("Cash");
  const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);
  const paymentDropdownRef = useRef(null);

  // Allocation type state (default: Automatic)
  const [allocationType, setAllocationType] = useState("Automatic");
  const [isManualAllocationModalOpen, setIsManualAllocationModalOpen] = useState(false);
  
  // Debug: Log allocation type changes
  useEffect(() => {
    console.log('📝 Allocation type state updated to:', allocationType);
  }, [allocationType]);
  
  // Expose modal opening function to parent component
  useEffect(() => {
    if (onOpenManualAllocationModal) {
      onOpenManualAllocationModal(() => {
        setIsManualAllocationModalOpen(true);
      });
    }
  }, [onOpenManualAllocationModal]);
  const [isAutomaticAllocationModalOpen, setIsAutomaticAllocationModalOpen] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);
  
  // Search and assignment state
  const [manualSearchPerformed, setManualSearchPerformed] = useState(false); // Track if manual search was performed
  const [showAutoAssignConfirmation, setShowAutoAssignConfirmation] = useState(false); // Confirmation modal for automatic flow
  const [autoAssignInProgress, setAutoAssignInProgress] = useState(false); // Track auto-assignment progress
  
  // Error modal state for automatic booking
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    title: "Validation Error",
    message: "",
  });
  
  // Error message state for manual booking (shown near search button)
  const [manualBookingError, setManualBookingError] = useState("");

  // Helper: Validate mobile number is exactly 10 digits
  const validateMobileNumber = useCallback((phone) => {
    if (!phone || !phone.trim()) {
      return { isValid: false, message: "Please enter valid mobile number" };
    }

    const phoneDigits = phone.replace(/\D/g, ""); // Remove all non-digits

    // Allow any 10 digit phone number
    if (phoneDigits.length !== 10) {
      return { isValid: false, message: "Please enter a valid 10-digit mobile number" };
    }

    return { isValid: true, message: "" };
  }, []);

  // Clear manual booking error when phone number changes
  useEffect(() => {
    if (manualBookingError && bookingFormData?.phone) {
      // Clear error if phone number is valid now
      const phoneValidation = validateMobileNumber(bookingFormData.phone);
      if (phoneValidation.isValid) {
        setManualBookingError("");
      }
    }
  }, [bookingFormData?.phone, manualBookingError, validateMobileNumber]);

  // Generate mock drivers for automatic allocation
  useEffect(() => {
    // Generate drivers when component mounts or when automatic allocation is selected
    if (availableDrivers.length === 0) {
      const mockDrivers = [
        {
          id: 'driver-1',
          name: 'Rajesh Kumar',
          phone: '+91 98765 43210',
          ambulanceNumber: 'AMB-001',
          distance: 2.5,
          rating: 4.8,
          status: 'available'
        },
        {
          id: 'driver-2',
          name: 'Suresh Patel', 
          phone: '+91 98765 43211',
          ambulanceNumber: 'AMB-002',
          distance: 3.2,
          rating: 4.6,
          status: 'available'
        },
        {
          id: 'driver-3',
          name: 'Amit Singh',
          phone: '+91 98765 43212',
          ambulanceNumber: 'AMB-003', 
          distance: 4.1,
          rating: 4.9,
          status: 'available'
        }
      ];
      setAvailableDrivers(mockDrivers);
    }
  }, []); // Run once on component mount

  // Use ref instead of state for fareCalculationKey to avoid re-render loops
  const fareCalculationKeyRef = useRef(null);
  const renderCountRef = useRef(0);

  // Initialize payment type from bookingFormData
  useEffect(() => {
    if (bookingFormData?.paymentType) {
      setPaymentType(bookingFormData.paymentType);
    }
  }, [bookingFormData?.paymentType]);

  // Notify parent when paymentType changes
  useEffect(() => {
    if (onPaymentTypeChange) {
      onPaymentTypeChange(paymentType);
    }
  }, [paymentType, onPaymentTypeChange]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        paymentDropdownRef.current &&
        !paymentDropdownRef.current.contains(event.target)
      ) {
        setIsPaymentDropdownOpen(false);
      }
      if (
        partnerDropdownRef.current &&
        !partnerDropdownRef.current.contains(event.target)
      ) {
        setIsPartnerDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Debug: track renders
  useEffect(() => {
    renderCountRef.current += 1;
    if (renderCountRef.current % 10 === 0) {
      console.warn(`🔄 ChooseAmbulancecardDuplicate has re-rendered ${renderCountRef.current} times`);
    }
  });

  // Helper function to get fallback ambulance types
  const getFallbackAmbulanceTypes = (prefix = "fallback") => [
    {
      type_id: `${prefix}-1`,
      name: "Basic Life Support",
      description: "Standard ambulance service",
      base_fare: 500,
      per_km_rate: 15
    },
    {
      type_id: `${prefix}-2`,
      name: "Advanced Life Support",
      description: "Advanced medical equipment",
      base_fare: 800,
      per_km_rate: 20
    }
  ];

  // Helper function to apply fallback types
  const applyFallbackTypes = (prefix = "fallback") => {
    const fallbackTypes = getFallbackAmbulanceTypes(prefix);
    console.log("🔄 [ChooseAmbulanceDuplicate] Applying fallback types:", fallbackTypes);
    setAllAmbulanceTypes(fallbackTypes);
    // Also set ambulanceTypes directly to ensure they display immediately
    setAmbulanceTypes(fallbackTypes);
    // The filteredAmbulanceTypes useMemo will update if needed
  };

  // Helper function to select API based on user role
  const selectAmbulanceTypesAPI = async () => {
    if (isFleetUser) {
      console.log("🚛 Loading ambulance types for fleet user");
      return await authService.getAmbulanceTypes();
    }
    if (isHospitalUser) {
      console.log("🏥 Loading ambulance types for hospital user");
      return await apiService.getAmbulanceTypes();
    }
    console.log("👤 Loading ambulance types for regular user");
    return await apiService.getAmbulanceTypes();
  };

  // Helper function to extract ambulance data from response
  const extractAmbulanceData = (response) => {
    console.log("🔍 [ChooseAmbulanceDuplicate] Extracting ambulance data from response:", response);
    
    if (!response) {
      console.warn("⚠️ Response is null or undefined");
      return null;
    }

    // Try multiple possible response structures
    if (Array.isArray(response.data)) {
      console.log("✅ Found array in response.data:", response.data.length, "items");
      return response.data;
    }
    if (response.data && Array.isArray(response.data.data)) {
      console.log("✅ Found array in response.data.data:", response.data.data.length, "items");
      return response.data.data;
    }
    if (response.data && Array.isArray(response.data.ambulance_types)) {
      console.log("✅ Found array in response.data.ambulance_types:", response.data.ambulance_types.length, "items");
      return response.data.ambulance_types;
    }
    if (Array.isArray(response)) {
      console.log("✅ Response is directly an array:", response.length, "items");
      return response;
    }

    console.warn("⚠️ Could not extract ambulance data from response structure");
    return null;
  };

  // Helper function to handle successful response
  const handleSuccessfulResponse = (response) => {
    console.log("✅ Raw ambulance types response:", response);
    const ambulanceData = extractAmbulanceData(response);

    if (!ambulanceData || ambulanceData.length === 0) {
      console.warn("⚠️ No ambulance types found in response:", response);
      setError("No ambulance types available");
      // Use fallback types if API returns empty
      applyFallbackTypes("api-empty-fallback");
      return;
    }

    // Deduplicate types by abbreviation or normalized category
    const seen = new Set();
    const deduplicated = ambulanceData.filter((t) => {
      const abbr = t.abbreviation || getAmbulanceTypeAbbreviation(t.name) || t.name;
      const key = String(abbr).trim().toUpperCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    console.log("✅ Ambulance types loaded successfully:", deduplicated.length, "types");
    console.log("✅ Sample ambulance type:", deduplicated[0]);
    console.log("✅ All ambulance types:", deduplicated);
    setAllAmbulanceTypes(deduplicated);
    // The filteredAmbulanceTypes useMemo and useEffect will update ambulanceTypes
    // This ensures filters are properly applied
  };

  // Helper function to handle error response
  const handleErrorResponse = (error) => {
    console.error("❌ Error loading ambulance types:", error);
    console.error("❌ Error details:", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });

    // Always use fallback types on error to ensure something is displayed
    console.warn("🚨 Error loading ambulance types, using fallback data");
    applyFallbackTypes("error-fallback");
    
    // Only show error message if we can't use fallback
    // setError("Failed to load ambulance types");
  };

  // Define fetchEstimateFares function to fetch ambulance types
  // NOTE: Ambulance types should be fetched on mount, regardless of coordinates
  const fetchEstimateFares = useCallback(async () => {
      console.log("🔄 [ChooseAmbulanceDuplicate] Fetching ambulance types...");
      setLoading(true);
      setError(""); // Clear any previous errors

      // Set a safety timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        console.warn("⏰ Ambulance types loading timeout, using fallback data");
        applyFallbackTypes("timeout-fallback");
        setLoading(false);
      }, 10000); // 10 second timeout

      try {
        const response = await selectAmbulanceTypesAPI();
        
        if (!response?.data) {
          console.warn("⚠️ Invalid response structure:", response);
          console.warn("⚠️ Response object:", response);
          // Use fallback types even on invalid response structure
          applyFallbackTypes("invalid-response-fallback");
          return;
        }

        handleSuccessfulResponse(response);
      } catch (error) {
        handleErrorResponse(error);
      } finally {
        clearTimeout(timeoutId); // Clear the safety timeout
        setLoading(false);
      }
  }, [isFleetUser, isHospitalUser]); // Removed coordinates from dependencies - fetch types on mount

  // Load ambulance types and fare estimates from new API
  useEffect(() => {
    fetchEstimateFares();
  }, [fetchEstimateFares]);

  // Filter ambulance types based on selected fleet partners (for hospital users)
  // DISABLED: Always show all ambulance types (filtering disabled)
  // NOTE: Removed setAmbulanceTypes call to avoid overriding filtered results
  useEffect(() => {
    const filterAmbulanceTypesByFleet = async () => {
      // Always show all ambulance types - skip filtering
      // Do not set ambulanceTypes here - let the filteredAmbulanceTypes useMemo handle it
      console.log("🔓 [FLEET FILTER] Showing all ambulance types - filtering disabled");
      setIsFiltered(false);
      setFilterInfo(null);
      return;
    };

    filterAmbulanceTypesByFleet();
  }, [bookingFormData?.fleetPartnerIds, bookingFormData?.hospitalPartnerIds]);

  // Initialize fleet partner names on mount
  useEffect(() => {
    setFleetPartnerNames(['Partner']);
  }, []);

  // Fetch fleet partner names for display
  useEffect(() => {
    const fetchFleetPartnerNames = async () => {
      // If no fleet partners selected, use empty operator map
      if (!bookingFormData?.fleetPartnerIds || bookingFormData.fleetPartnerIds.length === 0) {
        setFleetOperatorMap({});
        setFleetPartnerNames(['Partner']); // Set "Partner" as default partner name for the chip
        return;
      }

      try {
        const fleetPartnerIds = bookingFormData.fleetPartnerIds;
        const operatorMap = {};

        // Fetch fleet details for each partner ID (maintain order)
        const fleetPromises = fleetPartnerIds.map(async (fleetId) => {
          try {
            const response = await apiService.getFleetById(fleetId);
            const fleet = response?.data || response;
            const fleetName = fleet?.name || fleet?.fleet_name || 'Fleet Operator';

            return { fleetId, fleetName, fleet };
          } catch (error) {
            console.error(`Error fetching fleet ${fleetId}:`, error);
            return { fleetId, fleetName: 'Partner', fleet: null };
          }
        });

        const fleetData = await Promise.all(fleetPromises);
        // Maintain the order of fleetPartnerIds
        const orderedFleetNames = fleetPartnerIds.map(id => {
          const fleet = fleetData.find(f => String(f.fleetId) === String(id));
          return fleet?.fleetName || 'Partner';
        });
        setFleetPartnerNames(orderedFleetNames);

        // Fetch ambulances to map types to fleet operators
        const ambulancePromises = fleetPartnerIds.map(async (fleetId) => {
          try {
            const response = await apiService.getAmbulances({
              operator_id: fleetId,
              limit: 1000,
            });

            let ambulances = [];
            if (response?.data?.ambulances) {
              ambulances = response.data.ambulances;
            } else if (Array.isArray(response?.data)) {
              ambulances = response.data;
            } else if (Array.isArray(response)) {
              ambulances = response;
            }

            const fleetInfo = fleetData.find(f => f.fleetId === fleetId);
            const fleetName = fleetInfo?.fleetName || 'Fleet Operator';

            // Map each ambulance type to the fleet operator
            ambulances.forEach((ambulance) => {
              const typeId = ambulance.type_id || ambulance.type?.type_id;
              if (typeId && !operatorMap[typeId]) {
                operatorMap[typeId] = fleetName;
              }
            });

            return ambulances;
          } catch (error) {
            console.error(`Error fetching ambulances for fleet ${fleetId}:`, error);
            return [];
          }
        });

        await Promise.all(ambulancePromises);
        setFleetOperatorMap(operatorMap);
      } catch (error) {
        console.error('Error fetching fleet partner names:', error);
      }
    };

    fetchFleetPartnerNames();
  }, [bookingFormData?.fleetPartnerIds, ambulanceTypes]);

  // Extract available partners from hospital's linked fleet partners
  useEffect(() => {
    const extractPartnersFromFleetData = async () => {
      // Check if we have fleet partner IDs from booking form data (hospital's linked partners)
      if (bookingFormData?.fleetPartnerIds && bookingFormData.fleetPartnerIds.length > 0) {
        try {
          const fleetPartnerIds = bookingFormData.fleetPartnerIds;
          const partnerList = [];
          
          // Fetch details for each linked fleet partner
          const fleetPromises = fleetPartnerIds.map(async (fleetId) => {
            try {
              const response = await apiService.getFleetById(fleetId);
              const fleet = response?.data || response;
              const partnerName = fleet?.name || fleet?.fleet_name || `Partner ${fleetId}`;
              
              return {
                id: fleetId,
                name: partnerName,
                fleet: fleet
              };
            } catch (error) {
              console.error(`Error fetching fleet partner ${fleetId}:`, error);
              return {
                id: fleetId,
                name: `Partner ${fleetId}`,
                fleet: null
              };
            }
          });
          
          const fleetData = await Promise.all(fleetPromises);
          
          // Create partner list from hospital's linked fleet partners
          fleetData.forEach(fleetInfo => {
            if (fleetInfo) {
              partnerList.push({
                id: fleetInfo.id,
                name: fleetInfo.name
              });
            }
          });
          
          console.log("🤝 [ChooseAmbulanceDuplicate] Hospital's linked partners:", partnerList);
          setAvailablePartners(partnerList);
        } catch (error) {
          console.error("Error fetching hospital's fleet partners:", error);
          setAvailablePartners([]);
        }
      } else {
        // No fleet partners linked to hospital, or no booking form data
        console.log("🤝 [ChooseAmbulanceDuplicate] No fleet partners linked to hospital");
        setAvailablePartners([]);
      }
    };
    
    extractPartnersFromFleetData();
  }, [bookingFormData?.fleetPartnerIds]);

  // Helper function to initialize loading state
  const initializeLoadingState = () => {
    const loadingState = {};
    ambulanceTypes.forEach(type => {
      loadingState[type.type_id] = true;
    });
    setFareLoading(loadingState);
  };

  // Helper function to select fare estimation API based on user role
  const selectFareEstimationAPI = async (pickup, drop) => {
    if (isFleetUser) {
      console.log("🚛 Using fleet estimate fare API for fleet user");
      return await authService.fleetEstimateBookingFare({
        pickup_latitude: pickup.latitude,
        pickup_longitude: pickup.longitude,
        dropoff_latitude: drop.latitude,
        dropoff_longitude: drop.longitude
      });
    }
    if (isHospitalUser) {
      console.log("🏥 Using hospital estimate fare API (V2) for hospital user");
      const hospitalId = user?.hospital_id ?? user?.hospitalId ?? user?.hospital?.hospital_id ?? user?.hospital?.id;
      return await hospitalService.estimateFare(pickup, drop, { hospitalId });
    }
    console.log("👤 Using regular booking estimate fare API for regular user");
    return await bookingService.estimateFare(pickup, drop);
  };

  // Handle fleet automatic booking - directly trigger candidates API
  const handleFleetAutomaticBooking = async () => {
    console.log('🚛 [Fleet Automatic] Starting automatic booking flow');
    
    try {
      // Validate required data
      if (!bookingFormData) {
        throw new Error('Booking form data is missing');
      }
      if (!pickupCoordinates || !dropCoordinates) {
        throw new Error('Pickup and dropoff coordinates are required');
      }
      if (!bookingFormData.hospitalPartnerId) {
        throw new Error('Hospital partner ID is required for fleet bookings');
      }

      setLoading(true);
      setError("");
      setAutoAssignInProgress(true);

      // Determine payment method
      const paymentMethod = paymentType === "Online" ? "ONLINE" : 
                           paymentType === "Cash" ? "CASH" : "PAY_LATER";

      // Build candidates API request body
      const candidatesData = {
        hospitalId: bookingFormData.hospitalPartnerId,
        pickupLocation: {
          latitude: typeof pickupCoordinates.latitude === 'number' 
            ? pickupCoordinates.latitude 
            : parseFloat(pickupCoordinates.latitude),
          longitude: typeof pickupCoordinates.longitude === 'number'
            ? pickupCoordinates.longitude
            : parseFloat(pickupCoordinates.longitude),
          address: bookingFormData.pickupLocation || ""
        },
        dropoffLocation: {
          latitude: typeof dropCoordinates.latitude === 'number'
            ? dropCoordinates.latitude
            : parseFloat(dropCoordinates.latitude),
          longitude: typeof dropCoordinates.longitude === 'number'
            ? dropCoordinates.longitude
            : parseFloat(dropCoordinates.longitude),
          address: bookingFormData.dropLocation || ""
        },
        payment_method: paymentMethod
      };

      // Add optional filters
      // Add operator_id filter if partner is selected (not "all" or "all_partners")
      if (selectedPartner !== "all" && selectedPartner !== "all_partners") {
        let operatorId = null;
        if (typeof selectedPartner === 'string' && selectedPartner.includes('_')) {
          operatorId = selectedPartner.split('_')[0];
        } else {
          operatorId = selectedPartner;
        }
        if (operatorId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(operatorId)) {
          candidatesData.operator_id = operatorId;
        }
      }

      // Add distance range filter
      if (kmRange && kmRange > 0) {
        candidatesData.distance_range_km = kmRange;
      }

      // Add ambulance type filter
      if (selectedAmbulanceTypes.length > 0) {
        candidatesData.ambulance_type_ids = selectedAmbulanceTypes;
      }

      console.log('🔍 [Fleet Automatic] Calling candidates API with payload:', candidatesData);

      // Call fleet candidates API
      const candidatesResponse = await authService.fleetGetCandidates(candidatesData);

      if (!candidatesResponse.success || !candidatesResponse.data?.candidates) {
        throw new Error(candidatesResponse.message || 'Failed to fetch available ambulances');
      }

      // Check if no candidates are available
      if (candidatesResponse.data.candidates.length === 0) {
        const noCandidatesMessage = 'No ambulances are currently available near this location. Please try increasing your search distance or choosing a different ambulance type.';
        setError(noCandidatesMessage);
        setLoading(false);
        setAutoAssignInProgress(false);
        
        // Clear ambulance types
        setAmbulanceTypes([]);
        setAllAmbulanceTypes([]);
        
        // Notify parent component
        if (onStartAutoAssignment) {
          onStartAutoAssignment();
        }
        
        setTimeout(() => {
          if (onAmbulanceTypesChange) {
            onAmbulanceTypesChange([]);
          }
        }, 50);
        
        console.warn('⚠️ [Fleet Automatic] No candidates available:', noCandidatesMessage);
        return;
      }

      console.log('✅ [Fleet Automatic] Available ambulances fetched:', candidatesResponse.data.candidates.length);

      // Extract candidate IDs to pass to startAutoAllocation
      const candidateVehicleIds = candidatesResponse.data.candidates
        .map(c => c.vehicle_id)
        .filter(id => id != null);
      const candidateDriverIds = candidatesResponse.data.candidates
        .map(c => c.driver?.id)
        .filter(id => id != null);

      // Transform candidates to ambulance format for display
      const availableAmbulances = candidatesResponse.data.candidates.map(candidate => ({
        vehicle_id: candidate.vehicle_id,
        type_id: candidate.type?.id,
        name: candidate.type?.name || 'Ambulance',
        plate_number: candidate.plate_number,
        driver: {
          id: candidate.driver?.id,
          name: candidate.driver?.name || '',
          phone: candidate.driver?.phone || '',
          rating: candidate.driver?.rating || null,
          total_rides: candidate.driver?.total_rides
        },
        operator: {
          id: candidate.operator?.id,
          name: candidate.operator?.name,
          is_partner: candidate.operator?.is_partner
        },
        operator_id: candidate.operator?.id,
        eta_minutes: candidate.eta_minutes,
        distanceFromPickup: candidate.distance_from_pickup_km || null,
        estimated_fare: candidate.estimated_fare,
        is_partner: candidate.operator?.is_partner || false,
        operator_name: candidate.operator?.name,
        fleetId: candidate.operator?.id,
        fleet_id: candidate.operator?.id,
        fleetOperatorId: candidate.operator?.id,
        pricing_source: candidate.pricing_source ?? null,
        pricing_model: candidate.pricing_model ?? null,
        pricing_breakdown: candidate.pricing_breakdown ?? candidate.breakdown ?? null
      }));

      // Update ambulance types with available ambulances
      setAmbulanceTypes(availableAmbulances);
      setAllAmbulanceTypes(availableAmbulances);

      // Notify parent component to start auto-assignment
      if (onStartAutoAssignment) {
        onStartAutoAssignment();
      }

      // First, display candidates to the user
      // Use setTimeout to ensure state update completes before sending types
      setTimeout(() => {
        if (onAmbulanceTypesChange) {
          console.log("📤 [Fleet Automatic] Sending available ambulances to parent - candidates displayed");
          onAmbulanceTypesChange(availableAmbulances);
        }
      }, 50);

      // Build filters object for the booking payload
      const filters = {};
      
      // Add operator_id filter if partner is selected (not "all" or "all_partners")
      if (selectedPartner !== "all" && selectedPartner !== "all_partners") {
        let operatorId = null;
        if (typeof selectedPartner === 'string' && selectedPartner.includes('_')) {
          operatorId = selectedPartner.split('_')[0];
        } else {
          operatorId = selectedPartner;
        }
        if (operatorId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(operatorId)) {
          filters.operator_id = operatorId;
        }
      }

      // Add distance range filter
      if (kmRange && kmRange > 0) {
        filters.distance_range_km = kmRange;
      }

      // Add ambulance type filter
      if (selectedAmbulanceTypes.length > 0) {
        filters.ambulance_type_ids = selectedAmbulanceTypes;
      }

      // Build booking payload for startAutoAllocation - matching API specification
      const bookingPayload = {
        hospitalId: bookingFormData.hospitalPartnerId,
        patientName: bookingFormData.name || '',
        patientPhone: bookingFormData.phone || '',
        bookingDirection: journeyDirection || bookingFormData.direction || 'OUTBOUND',
        pickupLocation: {
          address: bookingFormData.pickupLocation || '',
          latitude: typeof pickupCoordinates.latitude === 'number' 
            ? pickupCoordinates.latitude 
            : parseFloat(pickupCoordinates.latitude),
          longitude: typeof pickupCoordinates.longitude === 'number'
            ? pickupCoordinates.longitude
            : parseFloat(pickupCoordinates.longitude)
        },
        dropoffLocation: {
          address: bookingFormData.dropLocation || '',
          latitude: typeof dropCoordinates.latitude === 'number'
            ? dropCoordinates.latitude
            : parseFloat(dropCoordinates.latitude),
          longitude: typeof dropCoordinates.longitude === 'number'
            ? dropCoordinates.longitude
            : parseFloat(dropCoordinates.longitude)
        },
        paymentMethod: paymentMethod,
        // Include filters object if it has any properties
        filters: Object.keys(filters).length > 0 ? filters : undefined
      };

      // Add online payment details if payment method is Online
      if (paymentMethod === "ONLINE") {
        const estimatedFare = fareEstimates && Object.keys(fareEstimates).length > 0
          ? Object.values(fareEstimates)[0]
          : null;
        
        bookingPayload.paymentDetails = {
          paymentGateway: "razorpay",
          currency: "INR",
          amount: estimatedFare || null,
          paymentMethod: "ONLINE",
          patientPhone: bookingFormData.phone || '',
          patientEmail: bookingFormData.email || null,
          patientName: bookingFormData.name || ''
        };
      }

      // Wait a moment to ensure candidates are displayed in UI before triggering auto-allocation
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('🔄 [Fleet Automatic] Automatically triggering auto-allocation API after candidates displayed');

      // Call fleet startAutoAllocation API - automatically triggered after candidates are displayed
      const autoAllocationResult = await authService.fleetStartAutoAllocation(bookingPayload);
      
      if (!autoAllocationResult.success || !autoAllocationResult.data) {
        throw new Error(autoAllocationResult.message || 'Failed to start auto allocation');
      }
      
      const bookingId = autoAllocationResult.data.booking_id;
      
      if (!bookingId) {
        throw new Error('No booking ID received from auto allocation API');
      }
      
      console.log('✅ [Fleet Automatic] Auto-allocation started successfully:', {
        booking_id: bookingId,
        response_id: autoAllocationResult.data.response_id,
        status: autoAllocationResult.data.status,
        expires_in_sec: autoAllocationResult.data.expires_in_sec,
        driver: autoAllocationResult.data.driver,
        vehicle: autoAllocationResult.data.vehicle,
        estimated_fare: autoAllocationResult.data.estimated_fare
      });
      
      // Update UI with real-time data from auto-allocation API response
      // Transform the response data to match the expected format for UI binding
      const allocationData = {
        booking_id: bookingId,
        response_id: autoAllocationResult.data.response_id,
        status: autoAllocationResult.data.status,
        expires_in_sec: autoAllocationResult.data.expires_in_sec,
        driver: autoAllocationResult.data.driver || null,
        vehicle: autoAllocationResult.data.vehicle || null,
        pickup_location: autoAllocationResult.data.pickup_location || null,
        dropoff_location: autoAllocationResult.data.dropoff_location || null,
        estimated_fare: autoAllocationResult.data.estimated_fare || null
      };
      
      console.log('📊 [Fleet Automatic] Real-time allocation data for UI binding:', allocationData);
      
      // Pass booking_id and initial allocation data to parent component
      // This will trigger polling and update the UI with real-time data
      if (onStartAutoAssignment) {
        if (typeof onStartAutoAssignment === 'function') {
          // Pass the transformed allocation data for immediate UI update
          onStartAutoAssignment(bookingId, allocationData);
        } else {
          onStartAutoAssignment();
        }
      }
      
      console.log('✅ [Fleet Automatic] Booking created with ID:', bookingId, '- UI updated with real-time data');

    } catch (error) {
      console.error('❌ [Fleet Automatic] Error in automatic booking:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          'Failed to start automatic booking';
      setError(errorMessage);
      setAutoAssignInProgress(false);
      setLoading(false);
      
      // Show error to user
      setErrorModal({
        isOpen: true,
        title: "Booking Error",
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract distance from response
  const extractDistance = (responseData) => {
    const backendDistance = responseData.distance_km ||
                          responseData.distance ||
                          responseData.distanceKm;

    if (backendDistance && backendDistance > 0) {
      console.log("📏 Backend distance available:", backendDistance, "km");
      // Distance is now calculated per ambulance in the filtering logic
    }
  };

  // Helper function to process fleet batch estimates
  const processFleetBatchEstimates = (estimates, newEstimates) => {
    estimates.forEach(estimate => {
      const typeId = estimate.type_id;
      const fare = estimate.estimated_total;
      if (typeId && fare) {
        newEstimates[typeId] = fare;
      }
    });

    // Enrich ambulance type objects with breakdown/pricing data from fare response
    setAmbulanceTypes(prev => prev.map(type => {
      const match = estimates.find(e => e.type_id === type.type_id);
      if (!match) return type;
      return {
        ...type,
        breakdown: match.breakdown || type.breakdown || null,
        pricing_source: match.pricing_source ?? type.pricing_source ?? null,
        model: match.model ?? type.model ?? null,
      };
    }));
    setAllAmbulanceTypes(prev => prev.map(type => {
      const match = estimates.find(e => e.type_id === type.type_id);
      if (!match) return type;
      return {
        ...type,
        breakdown: match.breakdown || type.breakdown || null,
        pricing_source: match.pricing_source ?? type.pricing_source ?? null,
        model: match.model ?? type.model ?? null,
      };
    }));
  };

  // Helper function to process fleet single estimate
  const processFleetSingleEstimate = (responseData, newEstimates) => {
    console.log("Processing fleet single estimate for type:", responseData.ambulance_type);
    const matchingType = ambulanceTypes.find(type =>
      type.name === responseData.ambulance_type ||
      type.type_id === responseData.ambulance_type
    );
    if (matchingType) {
      newEstimates[matchingType.type_id] = responseData.estimated_total;
    }
  };

  // Helper function to process fleet estimates
  const processFleetEstimates = (responseData, newEstimates) => {
    if (responseData.estimates && Array.isArray(responseData.estimates)) {
      processFleetBatchEstimates(responseData.estimates, newEstimates);
      return;
    }
    if (responseData.estimated_total && responseData.ambulance_type) {
      processFleetSingleEstimate(responseData, newEstimates);
      return;
    }
    console.warn("Unknown fleet estimate response format:", responseData);
  };

  // Helper function to update estimate with vendor preference logic
  const updateEstimateWithPreference = (newEstimates, vendorNames, typeId, fare, fleet) => {
    if (!newEstimates[typeId]) {
      newEstimates[typeId] = fare;
      if (fleet.operator_name) {
        vendorNames[typeId] = fleet.operator_name;
      }
      return;
    }

    const shouldUpdate = (fleet.is_partner && fare < newEstimates[typeId]) ||
                        (!fleet.is_partner && newEstimates[typeId] > fare);

    if (shouldUpdate) {
      newEstimates[typeId] = fare;
      if (fleet.operator_name) {
        vendorNames[typeId] = fleet.operator_name;
      }
    }
  };

  // Helper function to process hospital fleet estimates
  const processHospitalFleetEstimates = (fleets, newEstimates) => {
    console.log("Processing hospital fleet estimates:", fleets.length, "fleets");
    
    const partnerTypeIdsSet = new Set();
    const vendorNames = {};
    const partnersData = [];
    
    fleets.forEach(fleet => {
      if (!fleet.estimates || !Array.isArray(fleet.estimates)) {
        return;
      }

      // Extract partner information for dropdown
      const operatorId = fleet.operator_id;
      const operatorName = fleet.operator_name || 'Unknown Operator';
      const isPartner = fleet.is_partner === true;

      // Process each estimate for this fleet
      fleet.estimates.forEach(estimate => {
        const typeId = estimate.type_id;
        const fare = estimate.total ?? estimate.estimated_fare;
        const typeName = estimate.name || 'Unknown Type';

        if (!typeId || fare === null || fare === undefined) {
          console.warn("⚠️ [Estimate Fare] Skipping estimate with missing type_id or fare:", { typeId, fare, estimate });
          return;
        }

        if (isPartner) {
          partnerTypeIdsSet.add(typeId);
        }

        console.log("💰 [Estimate Fare] Processing estimate:", {
          typeId,
          fare,
          typeName,
          operator_name: operatorName,
          is_partner: isPartner
        });

        updateEstimateWithPreference(newEstimates, vendorNames, typeId, fare, fleet);

        // Add each ambulance type as a separate partner option for dropdown (V2: include total, breakdown, pricing_source, model)
        const uniqueKey = `${operatorId}_${typeId}`;
        partnersData.push({
          id: uniqueKey,
          operator_id: operatorId,
          operator_name: operatorName,
          is_partner: isPartner,
          type_id: typeId,
          type_name: typeName,
          estimated_fare: fare,
          total: estimate.total ?? fare,
          breakdown: estimate.breakdown ?? null,
          pricing_source: estimate.pricing_source ?? estimate.pricingSource ?? null,
          model: estimate.model ?? null,
        });
      });
    });
    
    setPartnerTypeIds(partnerTypeIdsSet);
    setVendorNamesMap(vendorNames);
    setPartnersFromEstimateFare(partnersData);
    console.log("✅ [Estimate Fare] Partner type IDs from estimate fare API:", Array.from(partnerTypeIdsSet));
    console.log("✅ [Estimate Fare] Vendor names from estimate fare API:", vendorNames);
    console.log("✅ [Estimate Fare] Partners data for dropdown:", partnersData);
    console.log("✅ [Estimate Fare] Final fare estimates object:", newEstimates);
    console.log("✅ [Estimate Fare] Fare estimate keys:", Object.keys(newEstimates));
    console.log("✅ [Estimate Fare] Fare estimate entries:", Object.entries(newEstimates));
    
    // Pass vendor names to parent component
    if (onVendorNamesChange) {
      console.log("📤 [Estimate Fare] Passing vendor names to parent:", vendorNames);
      onVendorNamesChange(vendorNames);
    }
  };

  // Helper function to process fallback single estimate
  const processFallbackEstimate = (responseData, newEstimates) => {
    const typeId = ambulanceTypes[0]?.type_id;
    if (typeId) {
      newEstimates[typeId] = responseData.estimated_total;
    }
  };

  // Helper function to process response data
  const processFareResponse = (response) => {
    if (!response.success || !response.data) {
      return;
    }

    const newEstimates = {};
    console.log("Raw fare estimation response:", response.data);

    extractDistance(response.data);

    const data = response.data;
    if (data && (data.distance_km != null || data.estimated_duration_minutes != null)) {
      setEstimateDistanceKm(data.distance_km != null ? Number(data.distance_km) : null);
      setEstimateDurationMinutes(data.estimated_duration_minutes != null ? Number(data.estimated_duration_minutes) : null);
    }

    if (isFleetUser) {
      processFleetEstimates(response.data, newEstimates);
    } else if (response.data.fleets && Array.isArray(response.data.fleets)) {
      processHospitalFleetEstimates(response.data.fleets, newEstimates);
    } else if (response.data.estimated_total) {
      processFallbackEstimate(response.data, newEstimates);
    }

    console.log("Processed fare estimates:", newEstimates);
    console.log("Current ambulance types:", ambulanceTypes.map(t => ({ id: t.type_id, name: t.name })));
    console.log("Fare estimate keys:", Object.keys(newEstimates));
    console.log("Fare estimate values:", Object.entries(newEstimates));

    setFareEstimates(newEstimates);
    if (onFareEstimatesChange) {
      console.log("📤 Passing fare estimates to parent:", newEstimates);
      onFareEstimatesChange(newEstimates);
    }
  };

  // Debounced fare calculation function
  const debouncedCalculateFares = useCallback(
    debounce(async (pickup, drop) => {
      if (!pickup || !drop) return;

      console.log("Debounced fare calculation triggered");

      setPartnerTypeIds(new Set());
      initializeLoadingState();

      try {
        const response = await selectFareEstimationAPI(pickup, drop);
        processFareResponse(response);
      } catch (error) {
        console.error("Error calculating fares:", error);
      } finally {
        setFareLoading({});
      }
    }, 500), // 500ms debounce
    [onFareEstimatesChange, ambulanceTypes, isFleetUser, isHospitalUser]
  );

  // Calculate fares when coordinates change (debounced)
  useEffect(() => {
    if (pickupCoordinates && dropCoordinates) {
      const calculationKey = `${pickupCoordinates.latitude}-${pickupCoordinates.longitude}-${dropCoordinates.latitude}-${dropCoordinates.longitude}`;
      if (fareCalculationKeyRef.current !== calculationKey) {
        fareCalculationKeyRef.current = calculationKey;
        debouncedCalculateFares(
          pickupCoordinates,
          dropCoordinates
        );
      }
    } else {
      setEstimateDistanceKm(null);
      setEstimateDurationMinutes(null);
    }
  }, [
    pickupCoordinates,
    dropCoordinates,
    debouncedCalculateFares,
  ]);

  // Handle ambulance selection - just select, don't auto-confirm
  const handleAmbulanceSelect = (ambulance) => {
    console.log("=== AMBULANCE SELECTION DEBUG ===");
    console.log("Selected ambulance:", ambulance);
    console.log("Ambulance type_id:", ambulance.type_id);
    console.log("Ambulance name:", ambulance.name);
    console.log("Current selectedAmbulance:", selectedAmbulance);

    // Just select the ambulance, user will click "Book Ambulance" button
    if (onAmbulanceSelect) {
      onAmbulanceSelect(ambulance);
    }
  };

  // Calculate estimated fare using backend API
  const calculateEstimatedFare = async (
    ambulanceType,
    pickupCoords,
    dropCoords
  ) => {
    if (ambulanceType?.type_id && fareEstimates[ambulanceType.type_id]) {
      return fareEstimates[ambulanceType.type_id];
    }

    // Fallback to frontend calculation
    const distance =
      pickupCoords && dropCoords
        ? calculateDistance(
          pickupCoords.latitude,
          pickupCoords.longitude,
          dropCoords.latitude,
          dropCoords.longitude
        )
        : 25; // Fallback distance if no coordinates available

    const baseFare = (ambulanceType?.base_fare || 0) * 0.7; // Reduced base fare
    const distanceFare = (ambulanceType?.per_km_rate || 0) * 0.8 * distance; // Reduced per km rate
    return Math.round(baseFare + distanceFare);
  };

  // Helper function to calculate distance (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    return Math.round(distance * 10) / 10; // Round to 1 decimal place
  };

  // Get the primary fleet partner name for the chip (preferred or first)
  const getPrimaryFleetPartnerName = () => {
    // Return the first fleet partner name from the response
    if (fleetPartnerNames && fleetPartnerNames.length > 0) {
      return fleetPartnerNames[0];
    }
    
    // Fallback: get from selected ambulance's operator_name
    if (selectedAmbulance?.operator_name) {
      return selectedAmbulance.operator_name;
    }

    // Fallback: get from first ambulance type
    if (ambulanceTypes.length > 0 && ambulanceTypes[0]?.operator_name) {
      return ambulanceTypes[0].operator_name;
    }

    return null;
  };

  const primaryFleetPartnerName = getPrimaryFleetPartnerName();

  // Helper function to get journey direction display text
  const getJourneyDirectionText = (direction) => {
    if (direction === "INBOUND") return "🔄 Inbound";
    return "➡️ Outbound";
  };

  // Helper function to get filter status colors
  const getFilterStatusColors = (filterType) => {
    if (filterType === 'hospital') {
      return {
        container: 'bg-green-50 border border-green-200',
        dot: 'bg-green-600',
        text: 'text-green-800',
        description: 'text-green-600'
      };
    }
    return {
      container: 'bg-blue-50 border border-blue-200',
      dot: 'bg-blue-600',
      text: 'text-blue-800',
      description: 'text-blue-600'
    };
  };

  // Helper function to get filter status text
  const getFilterStatusText = (filterInfo) => {
    if (filterInfo.filterType === 'hospital') {
      return {
        title: 'Filtered by Hospital Partners',
        partnerType: 'hospital(s)'
      };
    }
    return {
      title: 'Filtered by Fleet Partners',
      partnerType: 'fleet(s)'
    };
  };

  // Helper function to render filter status indicator
  const renderFilterStatusIndicator = () => {
    if (!isFiltered || !filterInfo) return null;

    const colors = getFilterStatusColors(filterInfo.filterType);
    const text = getFilterStatusText(filterInfo);

    return (
      <div className={`mb-3 p-2 rounded-lg ${colors.container}`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full animate-pulse ${colors.dot}`}></div>
          <span className={`text-xs font-semibold ${colors.text}`}>
            {text.title}
          </span>
        </div>
        <div className={`text-xs mt-1 ${colors.description}`}>
          Showing {filterInfo.filteredTypes} of {filterInfo.totalTypes} types from {filterInfo.selectedPartners} selected {text.partnerType}
        </div>
        {process.env.NODE_ENV === 'development' && (
          <div className="text-xs text-gray-500 mt-1 font-mono">
            [DEBUG] Check console for details
          </div>
        )}
      </div>
    );
  };

  // Apply filters to ambulance types with partner, distance, and type filtering
  const filteredAmbulanceTypes = useMemo(() => {
    // Start with all ambulance types
    let filtered = [...allAmbulanceTypes];

    // 1. Apply hospital's fleet partner restriction first
    // Only show ambulances from hospital's linked fleet partners
    if (bookingFormData?.fleetPartnerIds && bookingFormData.fleetPartnerIds.length > 0) {
      const linkedFleetIds = bookingFormData.fleetPartnerIds.map(id => String(id));
      
      filtered = filtered.filter(ambulance => {
        // Check multiple possible field names for fleet/partner ID
        const fleetId = ambulance.fleetId || ambulance.fleet_id || ambulance.operator_id || ambulance.fleetOperatorId;
        const partnerId = ambulance.partnerId || ambulance.partnerCode || ambulance.partner_id || ambulance.partner_code;
        
        // Check if ambulance belongs to any of the hospital's linked fleet partners
        return linkedFleetIds.includes(String(fleetId)) || linkedFleetIds.includes(String(partnerId));
      });
    }

    // 2. Apply specific partner filter (if not "all" or "all_partners")
    // When "all_partners" is selected, show ambulances from ALL partners
    if (selectedPartner !== "all" && selectedPartner !== "all_partners") {
      // Find the selected partner from estimate fare data
      const selectedPartnerData = partnersFromEstimateFare.find(p => p.id === selectedPartner);

      if (selectedPartnerData) {
        // Filter by both operator_id and type_id to match the selected partner option
        filtered = filtered.filter(ambulance => {
          const ambulanceOperatorId = ambulance.operator_id || ambulance.fleetId || ambulance.fleetOperatorId;
          const ambulanceTypeId = ambulance.type_id || ambulance.typeId || ambulance.id;

          return String(ambulanceOperatorId) === String(selectedPartnerData.operator_id) &&
                 String(ambulanceTypeId) === String(selectedPartnerData.type_id);
        });
      } else {
        // Fallback to old logic for availablePartners
        filtered = filtered.filter(ambulance => {
          const partnerId = ambulance.partnerId || ambulance.partnerCode || ambulance.partner_id || ambulance.partner_code || ambulance.fleetOperatorId || ambulance.fleetId || ambulance.operator_id;
          const partnerName = ambulance.partnerName || ambulance.partner_name || ambulance.fleetOperatorName || ambulance.fleetName || ambulance.operator_name || ambulance.vendor_name;

          return String(partnerId) === String(selectedPartner) || partnerName === selectedPartner;
        });
      }
    }

    // 2. Add distance calculation for each ambulance if pickup coordinates are available
    if (pickupCoordinates && pickupCoordinates.latitude && pickupCoordinates.longitude) {
      filtered = filtered.map(ambulance => {
        // Calculate distance from pickup location to ambulance location
        let distanceFromPickup = null;
        if (ambulance.latitude && ambulance.longitude) {
          distanceFromPickup = calculateDistance(
            pickupCoordinates.latitude,
            pickupCoordinates.longitude,
            ambulance.latitude,
            ambulance.longitude
          );
        }
        
        return {
          ...ambulance,
          distanceFromPickup
        };
      });

      // 3. Filter by KM range - only show ambulances within the selected range
      filtered = filtered.filter(ambulance => {
        if (ambulance.distanceFromPickup === null) {
          // If no location data, include in results (fallback)
          return true;
        }
        return ambulance.distanceFromPickup <= kmRange;
      });

      // 4. Sort by distance (nearest first)
      filtered.sort((a, b) => {
        // Ambulances without location data go to the end
        if (a.distanceFromPickup === null && b.distanceFromPickup === null) return 0;
        if (a.distanceFromPickup === null) return 1;
        if (b.distanceFromPickup === null) return -1;
        
        // Sort by distance ascending (nearest first)
        return a.distanceFromPickup - b.distanceFromPickup;
      });
    }

    // 5. Apply ambulance type filter (multiple selection with OR condition)
    if (selectedAmbulanceTypes.length > 0) {
      filtered = filtered.filter(ambulance => {
        // Check multiple possible field names for ambulance type
        const ambulanceTypeId = ambulance.type_id || ambulance.typeId || ambulance.id;
        const ambulanceTypeName = ambulance.name || ambulance.type || ambulance.typeName;
        const ambulanceType = ambulance.type || ambulance.ambulanceType;
        
        return selectedAmbulanceTypes.some(selectedType => 
          selectedType === ambulanceTypeId ||
          selectedType === ambulanceTypeName ||
          selectedType === ambulanceType ||
          // Also check if the selected type matches any of the ambulance's identifiers
          ambulanceTypeId === selectedType ||
          ambulanceTypeName === selectedType ||
          ambulanceType === selectedType
        );
      });
    }

    // 6. Attach is_partner and operator_name from estimate fare API response
    filtered = filtered.map(ambulance => {
      const typeId = ambulance.type_id || ambulance.typeId || ambulance.id;
      const isPartner = partnerTypeIds.has(typeId);
      const operatorName = vendorNamesMap[typeId];
      
      return {
        ...ambulance,
        is_partner: isPartner || ambulance.is_partner || false,
        operator_name: operatorName || ambulance.operator_name || null
      };
    });

    return filtered;
  }, [allAmbulanceTypes, bookingFormData?.fleetPartnerIds, selectedPartner, kmRange, selectedAmbulanceTypes, pickupCoordinates, partnerTypeIds, vendorNamesMap, partnersFromEstimateFare]);

  // Update displayed ambulance types when filters change or when allAmbulanceTypes is updated
  useEffect(() => {
    console.log("🔄 [ChooseAmbulanceDuplicate] Updating ambulanceTypes from filteredAmbulanceTypes:", {
      filteredCount: filteredAmbulanceTypes.length,
      allCount: allAmbulanceTypes.length,
      linkedFleetPartners: bookingFormData?.fleetPartnerIds || [],
      selectedPartner,
      kmRange,
      selectedAmbulanceTypes,
      pickupCoordinates,
      filteredTypes: filteredAmbulanceTypes.map(t => ({ 
        id: t.type_id, 
        name: t.name, 
        distance: t.distanceFromPickup ? `${t.distanceFromPickup.toFixed(1)}km` : 'N/A' 
      }))
    });
    
    // Always update from filtered types
    // If filters result in empty but we have active filters, still show the empty array
    // This allows the parent to show a "no results" message instead of hiding the UI
    if (filteredAmbulanceTypes.length > 0) {
      setAmbulanceTypes(filteredAmbulanceTypes);
    } else if (allAmbulanceTypes.length > 0) {
      // If we have data but filters result in empty, check if filters are active
      const hasActiveFilters = (selectedPartner !== "all" && selectedPartner !== "all_partners") || kmRange < 50 || selectedAmbulanceTypes.length > 0;
      if (!hasActiveFilters) {
        // No active filters but filtered is empty - show all types as fallback
        console.warn("⚠️ No active filters but filtered result is empty - showing all types");
        setAmbulanceTypes(allAmbulanceTypes);
      } else {
        // Filters are active and resulting in empty - send empty array but keep the structure
        // This ensures the parent knows filters are active and can show appropriate UI
        console.log("⚠️ Active filters resulted in empty results - sending empty array to parent");
        setAmbulanceTypes([]);
      }
    } else {
      // No data loaded yet
      setAmbulanceTypes([]);
    }
  }, [filteredAmbulanceTypes, allAmbulanceTypes, bookingFormData?.fleetPartnerIds, selectedPartner, kmRange, selectedAmbulanceTypes, pickupCoordinates]);

  // Send ambulance types to parent component when they change
  // Only send non-empty arrays after auto-assignment is confirmed to prevent clearing the UI
  useEffect(() => {
    if (onAmbulanceTypesChange) {
      // Only send types if auto-assignment is in progress (after confirmation) and we have types
      // This prevents empty arrays from clearing the UI after confirmation
      if (autoAssignInProgress && ambulanceTypes.length > 0) {
        onAmbulanceTypesChange(ambulanceTypes);
      } else if (!autoAssignInProgress && ambulanceTypes.length > 0) {
        onAmbulanceTypesChange(ambulanceTypes);
      }
    }
  }, [ambulanceTypes, onAmbulanceTypesChange, autoAssignInProgress]);

  // Notify parent component when filters change
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange({
        selectedPartner,
        kmRange,
        selectedAmbulanceTypes
      });
    }
  }, [selectedPartner, kmRange, selectedAmbulanceTypes, onFiltersChange]);
  
  // Reset auto-assignment state when allocation type changes or is cleared
  // This ensures fresh start when user cancels and tries again
  useEffect(() => {
    if (allocationType === "") {
      console.log("🔄 [ChooseAmbulanceDuplicate] Allocation type cleared - resetting auto-assignment state");
      setAutoAssignInProgress(false);
      setShowAutoAssignConfirmation(false);
      setLoading(false);
      setError("");
      setAmbulanceTypes([]);
      setAllAmbulanceTypes([]);
    }
  }, [allocationType]);

  // Helper function to render loading state
  const renderLoadingState = () => (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      <span className="ml-2 text-gray-600">Loading ambulances...</span>
    </div>
  );

  // Helper function to render error state
  const renderErrorState = () => (
    <div className="flex items-center justify-center py-8">
      <div className="text-red-600 text-sm">{error}</div>
    </div>
  );

  // Helper function to render empty state
  const renderEmptyState = () => (
    <div className="flex items-center justify-center py-8">
      <div className="text-center text-gray-500">
        <p className="text-sm">No ambulances available</p>
        <p className="text-xs mt-1">Please check back later</p>
      </div>
    </div>
  );

  // Helper function to get ambulance card className
  const getAmbulanceCardClassName = (isSelected) => {
    if (isSelected) {
      return "relative flex items-start gap-3 border rounded-lg p-2.5 cursor-pointer transition-all overflow-hidden border-blue-500 bg-blue-50";
    }
    return "relative flex items-start gap-3 border rounded-lg p-2.5 cursor-pointer transition-all overflow-hidden border-gray-200 hover:bg-gray-50";
  };

  // Helper function to get ambulance name className
  const getAmbulanceNameClassName = (isSelected) => {
    return `font-semibold text-sm leading-tight break-words ${isSelected ? "text-blue-900" : "text-gray-900"}`;
  };

  // Helper function to get fare display className
  const getFareDisplayClassName = (isSelected) => {
    return `font-bold text-sm whitespace-nowrap ${isSelected ? "text-blue-900" : "text-gray-900"}`;
  };

  // Helper function to render fare display
  const renderFareDisplay = (isLoadingFare, estimatedFare) => {
    if (isLoadingFare) {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    }
    if (estimatedFare) {
      return `₹${Math.round(estimatedFare)}`;
    }
    return "₹--";
  };

  // Helper function to render ambulance card
  const renderAmbulanceCard = (ambulance, index) => {
    const isSelected = selectedAmbulance?.type_id === ambulance.type_id;
    const apiEstimate = fareEstimates[ambulance.type_id];
    const estimatedFare = apiEstimate || null;
    const isLoadingFare = fareLoading[ambulance.type_id];
    const vendorName = vendorNamesMap[ambulance.type_id];
    
    // Debug logging for fare estimates
    if (index === 0 && ambulance.type_id) {
      console.log("🔍 [Fare Display Debug] Ambulance:", {
        type_id: ambulance.type_id,
        name: ambulance.name,
        fareEstimates: fareEstimates,
        apiEstimate: apiEstimate,
        estimatedFare: estimatedFare,
        allFareEstimateKeys: Object.keys(fareEstimates),
        vendorName: vendorName
      });
    }

    return (
      <div
        key={ambulance.type_id || index}
        className={getAmbulanceCardClassName(isSelected)}
        onClick={() => handleAmbulanceSelect(ambulance)}
      >
        {/* Ambulance Icon */}
        <div className="flex-shrink-0">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center overflow-hidden">
            <img
              src={ambulanceImages[index % ambulanceImages.length]}
              alt={ambulance.name}
              className="w-full h-full object-contain"
            />
          </div>
        </div>
        {/* Ambulance Details */}
        <div className="flex-1 flex items-start justify-between gap-2 min-w-0 overflow-hidden">
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center gap-2">
              <p className={getAmbulanceNameClassName(isSelected)}>
                {ambulance.name}
              </p>
              {(ambulance.is_partner || partnerTypeIds.has(ambulance.type_id)) && (
                <span className="inline-flex items-center bg-green-50 border border-green-200 rounded-full px-2 py-0.5 text-xs font-medium text-green-700 whitespace-nowrap">
                  Partner
                </span>
              )}
            </div>
            {vendorName && (
              <p className="text-xs text-blue-600 font-medium mt-0.5 leading-relaxed break-words">
                {vendorName}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed break-words">
              Professional ambulance service with trained medical staff.
            </p>
            {ambulance.distanceFromPickup && (
              <p className="text-xs text-blue-600 font-medium mt-0.5">
                📍 {ambulance.distanceFromPickup.toFixed(1)} km away
              </p>
            )}
          </div>
          <div className="text-right flex-shrink-0 ml-2">
            <div className={getFareDisplayClassName(isSelected)}>
              {renderFareDisplay(isLoadingFare, estimatedFare)}
            </div>
            <div className="text-xs text-gray-500 whitespace-nowrap">Est. Fare</div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPricingInfoData({
                  pricing_source: ambulance.pricing_source ?? null,
                  pricing_model: ambulance.model ?? null,
                  pricing_breakdown: ambulance.breakdown ?? null,
                });
              }}
              className="mt-1 p-0.5 rounded-full text-gray-400 hover:text-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-400"
              title="Pricing breakdown"
              aria-label="Pricing breakdown"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </button>
            {isSelected && (
              <div className="text-xs text-blue-600 font-medium mt-1 whitespace-nowrap">
                Selected
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render filter controls
  const renderFilters = () => {
    return (
      <div className="mb-2 space-y-2 flex-shrink-0">
        {/* Select Partner Filter - Dropdown - Hidden for fleet dashboard instant booking */}
        {!isFleetUser && (
        <div className="relative" ref={partnerDropdownRef}>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Select Partner
          </label>
          <button
            type="button"
            onClick={() => setIsPartnerDropdownOpen(!isPartnerDropdownOpen)}
            className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white text-left flex items-center justify-between"
          >
            <span className="text-gray-700 flex items-center gap-2">
              {selectedPartner === "all"
                ? "Select Partner"
                : selectedPartner === "all_partners"
                ? (() => {
                    const uniqueCount = partnersFromEstimateFare.length > 0
                      ? new Set(partnersFromEstimateFare.map(p => p.operator_id)).size
                      : availablePartners.length;
                    return (
                      <>
                        <span className="font-semibold text-purple-700">All Partners</span>
                        <span className="inline-flex items-center bg-purple-100 border border-purple-200 rounded-full px-2 py-0.5 text-xs font-medium text-purple-700 whitespace-nowrap">
                          {uniqueCount} Selected
                        </span>
                      </>
                    );
                  })()
                : (() => {
                    // Try to find in partnersFromEstimateFare first
                    // Check by exact ID match or by operator_id match (for deduplicated display)
                    let partnerFromEstimate = partnersFromEstimateFare.find(p => p.id === selectedPartner);

                    // If not found by exact ID, try to find by operator_id (for deduplicated partners)
                    if (!partnerFromEstimate && typeof selectedPartner === 'string' && selectedPartner.includes('_')) {
                      const operatorId = selectedPartner.split('_')[0];
                      partnerFromEstimate = partnersFromEstimateFare.find(p => String(p.operator_id) === operatorId);
                    }

                    if (partnerFromEstimate) {
                      return (
                        <>
                          <span>{partnerFromEstimate.operator_name}</span>
                          {partnerFromEstimate.is_partner && (
                            <span className="inline-flex items-center bg-green-50 border border-green-200 rounded-full px-2 py-0.5 text-xs font-medium text-green-700 whitespace-nowrap">
                              Partner
                            </span>
                          )}
                        </>
                      );
                    }
                    // Fallback to availablePartners
                    const partner = availablePartners.find(p => p.id === selectedPartner);
                    return partner?.name || "Select Partner";
                  })()}
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
              isPartnerDropdownOpen ? "rotate-180" : ""
            }`} />
          </button>

          {isPartnerDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-[400px] overflow-y-auto">
              {/* Select All Partners Option */}
              {(partnersFromEstimateFare.length > 1 || availablePartners.length > 1) && (
                <button
                  type="button"
                  className={`w-full px-3 py-2.5 text-sm text-left hover:bg-purple-50 transition-colors duration-200 flex items-center justify-between border-b-2 border-purple-200 ${
                    selectedPartner === "all_partners"
                      ? "bg-purple-50 text-purple-700"
                      : "text-gray-700 bg-purple-50/30"
                  }`}
                  onClick={() => {
                    setSelectedPartner("all_partners");
                    setIsPartnerDropdownOpen(false);
                  }}
                >
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className="font-semibold">Select All Partners</span>
                    <span className="inline-flex items-center bg-purple-100 border border-purple-200 rounded-full px-2 py-0.5 text-xs font-medium text-purple-700 whitespace-nowrap flex-shrink-0">
                      {(() => {
                        const uniqueCount = partnersFromEstimateFare.length > 0
                          ? new Set(partnersFromEstimateFare.map(p => p.operator_id)).size
                          : availablePartners.length;
                        return `${uniqueCount} Partners`;
                      })()}
                    </span>
                  </div>
                  {selectedPartner === "all_partners" && (
                    <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0 ml-2" />
                  )}
                </button>
              )}

              {partnersFromEstimateFare.length > 0 ? (() => {
                // Deduplicate partners by operator_id to show each vendor only once
                const uniquePartners = new Map();
                partnersFromEstimateFare.forEach((partner) => {
                  const operatorId = partner.operator_id;
                  if (!uniquePartners.has(operatorId)) {
                    // Store the first occurrence of each operator
                    uniquePartners.set(operatorId, partner);
                  } else {
                    // If operator already exists, prefer the one with is_partner = true
                    const existing = uniquePartners.get(operatorId);
                    if (partner.is_partner && !existing.is_partner) {
                      uniquePartners.set(operatorId, partner);
                    }
                  }
                });

                const deduplicatedPartners = Array.from(uniquePartners.values());

                return deduplicatedPartners.map((partner) => {
                  // Check if this operator is selected (match by operator_id)
                  const isSelected = selectedPartner === partner.id || 
                                   (typeof selectedPartner === 'string' && 
                                    selectedPartner.includes('_') && 
                                    selectedPartner.split('_')[0] === String(partner.operator_id));
                  
                  return (
                    <button
                      key={partner.operator_id}
                      type="button"
                      className={`w-full px-3 py-2.5 text-sm text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between border-b border-gray-100 ${
                        isSelected
                          ? "bg-blue-50 text-blue-700"
                          : "text-gray-700"
                      }`}
                      onClick={() => {
                        // Use the first partner entry for this operator as the selection ID
                        // This ensures we select by operator_id
                        setSelectedPartner(partner.id);
                        setIsPartnerDropdownOpen(false);
                      }}
                    >
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <span className="font-medium truncate">{partner.operator_name}</span>
                        {partner.is_partner && (
                          <span className="inline-flex items-center bg-green-50 border border-green-200 rounded-full px-2 py-0.5 text-xs font-medium text-green-700 whitespace-nowrap flex-shrink-0">
                            Partner
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 ml-2" />
                      )}
                    </button>
                  );
                });
              })() : availablePartners.length > 0 ? (
                availablePartners.map((partner) => (
                  <button
                    key={partner.id}
                    type="button"
                    className={`w-full px-3 py-2 text-sm text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between ${
                      selectedPartner === partner.id
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                    onClick={() => {
                      setSelectedPartner(partner.id);
                      setIsPartnerDropdownOpen(false);
                    }}
                  >
                    <span>{partner.name}</span>
                    {selectedPartner === partner.id && (
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                    )}
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                  No partners available. Select pickup and dropoff locations to see partners.
                </div>
              )}
            </div>
          )}
        </div>
        )}
        {/* KM Range Slider */}
        <div className="relative">
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Distance Range: {kmRange} km
          </label>
          <div className="px-2">
            <input
              type="range"
              min="1"
              max="100"
              value={kmRange}
              onChange={(e) => setKmRange(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${kmRange}%, #e5e7eb ${kmRange}%, #e5e7eb 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>1 km</span>
              <span>100 km</span>
            </div>
          </div>
        </div>

        {/* Ambulance Type Filter - Checkboxes */}
        <div className="relative">
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-semibold text-gray-700">
              Filter by Ambulance Type
              {selectedAmbulanceTypes.length > 0 && (
                <span className="ml-2 text-xs text-blue-600 font-medium">
                  ({selectedAmbulanceTypes.length} selected)
                </span>
              )}
            </label>
            <button
              type="button"
              onClick={() => {
                setSelectedAmbulanceTypes([]);
                console.log('🔄 [Filter] Ambulance type filter cleared - showing all ambulances');
              }}
              className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              title="Refresh filter - Show all ambulances"
            >
              <svg
                className="w-4 h-4 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
          <p className="text-xs text-red-600 italic mb-1.5">
            Select at least one ambulance type( Mandatory )*
          </p>
          <div className="border-2 border-gray-200 rounded-lg p-3 bg-gray-50 max-h-[200px] overflow-y-auto">
            {(() => {
              // Create unique ambulance types list based on type_id to avoid duplicates
              const uniqueTypes = allAmbulanceTypes.reduce((acc, type) => {
                // Check if this type_id already exists in the accumulator
                const existingType = acc.find(t => t.type_id === type.type_id);
                if (!existingType && type.type_id) {
                  acc.push({
                    type_id: type.type_id,
                    name: type.name,
                    abbreviation: type.abbreviation || null
                  });
                }
                return acc;
              }, []);

              return uniqueTypes.length > 0 ? (
                <div className="space-y-2">
                  {uniqueTypes.map((type) => {
                    const shortName = type.abbreviation || getAmbulanceTypeAbbreviation(type.name) || type.name;
                    return (
                      <label
                        key={type.type_id}
                        className="flex items-center space-x-2 cursor-pointer hover:bg-white rounded p-1 transition-colors duration-200"
                        title={type.name}
                      >
                        <input
                          type="checkbox"
                          checked={selectedAmbulanceTypes.includes(type.type_id)}
                          onChange={(e) => handleAmbulanceTypeChange(type.type_id, e.target.checked, setSelectedAmbulanceTypes)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <span className="text-sm font-medium text-gray-700 select-none">
                          {shortName}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs text-gray-500 text-center py-2">
                  No ambulance types available
                </div>
              );
            })()}
            {selectedAmbulanceTypes.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setSelectedAmbulanceTypes([])}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear all ({selectedAmbulanceTypes.length} selected)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
    <div className={`bg-white rounded-2xl shadow-lg p-4 w-full max-w-sm flex flex-col relative ${isFleetUser ? 'self-start' : 'self-stretch'}`}>
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <h2 className="text-base font-semibold">Configure Booking</h2>
        <div className="flex flex-col items-end gap-1">
          {/* Distance badge - pill, green */}
          <span className="inline-flex items-baseline rounded-full px-2.5 py-0.5 bg-green-100 text-green-700 text-sm">
            <span className="font-semibold">
              {estimateDistanceKm != null
                ? Number(estimateDistanceKm).toFixed(1)
                : ambulanceTypes.length > 0 && ambulanceTypes[0]?.distanceFromPickup != null
                  ? ambulanceTypes[0].distanceFromPickup.toFixed(1)
                  : pickupCoordinates && dropCoordinates
                    ? '—'
                    : '…'}
            </span>
            <span className="ml-0.5 font-normal opacity-90">km</span>
          </span>
          {/* Duration badge - purple pill, tiny text */}
          {estimateDurationMinutes != null && (
            <span className="inline-flex items-baseline rounded-full px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] leading-tight">
              <span className="font-semibold">{Math.round(estimateDurationMinutes)}</span>
              <span className="ml-0.5 font-normal opacity-90">min</span>
            </span>
          )}
          {/* Journey direction badge - blue pill + arrow */}
          {journeyDirection && (
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium">
              {journeyDirection === "INBOUND" ? (
                <RotateCcw className="w-3 h-3 flex-shrink-0" />
              ) : (
                <ArrowRight className="w-3 h-3 flex-shrink-0" />
              )}
              {journeyDirection === "INBOUND" ? "Inbound" : "Outbound"}
            </span>
          )}
        </div>
      </div>

      <div className="flex-shrink-0">
        {renderFilterStatusIndicator()}
      </div>

      {/* Filters Section */}
      <div className="flex-shrink-0 mb-2">
        {renderFilters()}
      </div>

      {/* Allocation Type Selection */}
      <div className="mt-1 flex-shrink-0">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Allocation Type
        </label>
        <div className="grid grid-cols-2 gap-2">
          {/* Automatic Option */}
          <label className={`flex flex-col items-center justify-center cursor-pointer p-2 border-2 rounded-lg transition-colors duration-200 h-16 ${
            allocationType === "Automatic"
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50"
          }`}>
            <input
              type="radio"
              name="allocationType"
              value="Automatic"
              checked={allocationType === "Automatic"}
              onChange={(e) => {
                const newType = e.target.value;
                console.log('🔄 Allocation type changed to:', newType);
                setAllocationType(newType);
                if (onAllocationTypeChange) {
                  onAllocationTypeChange(newType);
                }
              }}
              className="sr-only"
            />
            <div className={`text-base mb-1 ${
              allocationType === "Automatic" ? "text-blue-700" : "text-gray-700"
            }`}>
              🔄
            </div>
            <div className={`text-xs font-medium text-center ${
              allocationType === "Automatic" ? "text-blue-700" : "text-gray-700"
            }`}>
              Automatic
            </div>
          </label>

          {/* Manual Option */}
          <label className={`flex flex-col items-center justify-center cursor-pointer p-2 border-2 rounded-lg transition-colors duration-200 h-16 ${
            allocationType === "Manual"
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50"
          }`}>
            <input
              type="radio"
              name="allocationType"
              value="Manual"
              checked={allocationType === "Manual"}
              onChange={(e) => {
                const newType = e.target.value;
                console.log('🔄 Allocation type changed to:', newType);
                setAllocationType(newType);
                if (onAllocationTypeChange) {
                  onAllocationTypeChange(newType);
                }
              }}
              className="sr-only"
            />
            <div className={`text-base mb-1 ${
              allocationType === "Manual" ? "text-blue-700" : "text-gray-700"
            }`}>
              👤
            </div>
            <div className={`text-xs font-medium text-center ${
              allocationType === "Manual" ? "text-blue-700" : "text-gray-700"
            }`}>
              Manual
            </div>
          </label>
        </div>
      </div>

      {/* Payment Type Selection */}
      <div className="mt-4 flex-shrink-0">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Payment Method
        </label>
        <div className="relative" ref={paymentDropdownRef}>
          <button
            type="button"
            onClick={() => setIsPaymentDropdownOpen(!isPaymentDropdownOpen)}
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-gray-50 hover:bg-white text-left flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              {paymentType === "Cash" && "💵 Cash"}
              {paymentType === "Online" && "💳 Online"}
            </span>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                isPaymentDropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isPaymentDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
              <PaymentDropdownButton
                value="Cash"
                label="Cash"
                icon="💵"
                currentPaymentType={paymentType}
                onSelect={(value) => {
                  setPaymentType(value);
                }}
                onClose={() => setIsPaymentDropdownOpen(false)}
              />
              <PaymentDropdownButton
                value="Online"
                label="Online"
                icon="💳"
                currentPaymentType={paymentType}
                onSelect={(value) => {
                  setPaymentType(value);
                }}
                onClose={() => setIsPaymentDropdownOpen(false)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Error Display - Only show when error exists */}
      {error && (
        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex-shrink-0">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-yellow-800">{error}</p>
          </div>
        </div>
      )}

      {/* Search/Book Button - Reserve space to prevent layout shift */}
      <div className="mt-2 flex-shrink-0">
        {allocationType === "Automatic" && selectedAmbulance && autoAssignInProgress ? (
          // Show selected ambulance name with Book button for automatic allocation (only when auto-assignment is in progress)
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs text-gray-600 mb-1">Selected Ambulance</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedAmbulance.name || selectedAmbulance.type}</p>
                </div>
              </div>
            </div>
            <button
              className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-4 rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-colors duration-200 transform hover:scale-[1.02]"
              onClick={() => {
              console.log('📖 Book button clicked for automatic allocation');
              console.log('🚑 Selected ambulance:', selectedAmbulance);
              
              if (!bookingFormData || !selectedAmbulance) {
                console.log('❌ Missing booking data or ambulance');
                return;
              }
              
              // Update bookingFormData with current payment type and automatic allocation
              const updatedFormData = {
                ...bookingFormData,
                paymentType: paymentType,
                allocationType: allocationType,
              };
              
              // Trigger confirmation modal
              if (onConfirmSelection) {
                onConfirmSelection(selectedAmbulance, updatedFormData);
              }
            }}
            disabled={!bookingFormData || !selectedAmbulance}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
              Book {selectedAmbulance.name || "Ambulance"}
            </button>
          </div>
        ) : (
          // Show search button for initial state or manual allocation
          <button
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-colors duration-200 transform hover:scale-[1.02]"
            onClick={() => {
          console.log('🔍 Search button clicked');
          console.log('🚑 Selected ambulance:', selectedAmbulance);
          console.log('📋 Booking form data:', bookingFormData);
          console.log('🔍 Allocation type:', allocationType);
          console.log('🚑 Available ambulance types:', ambulanceTypes.length);
          
          if (!bookingFormData) {
            console.log('❌ No booking form data');
            return;
          }

          // Validate mobile number before proceeding
          const hasPhone = bookingFormData.phone && bookingFormData.phone.trim();
          const hasEmail = bookingFormData.email && bookingFormData.email.trim();
          
          // If phone is provided, validate it's exactly 10 digits
          if (hasPhone) {
            const phoneValidation = validateMobileNumber(bookingFormData.phone);
            if (!phoneValidation.isValid) {
              if (allocationType === "Automatic") {
                // Show error modal for automatic booking
                setErrorModal({
                  isOpen: true,
                  title: "Validation Error",
                  message: phoneValidation.message,
                });
              } else if (allocationType === "Manual") {
                // Show error message near button for manual booking
                setManualBookingError(phoneValidation.message);
              }
              return;
            }
          } else if (!hasEmail) {
            // No phone and no email
            if (allocationType === "Automatic") {
              setErrorModal({
                isOpen: true,
                title: "Validation Error",
                message: "Please enter valid mobile number",
              });
            } else if (allocationType === "Manual") {
              setManualBookingError("Please enter valid mobile number");
            }
            return;
          }
          
          // Clear any previous manual booking error
          setManualBookingError("");
          
          // Update bookingFormData with current payment type
          const updatedFormData = {
            ...bookingFormData,
            paymentType: paymentType,
            allocationType: allocationType,
          };
          
          console.log('� Updated foarm data:', updatedFormData);
          console.log('🔍 Current allocation type:', allocationType);
          console.log('🚑 Available drivers count:', availableDrivers.length);
          
          if (allocationType === "Manual") {
            // Manual flow: Call candidates API and show ambulance list
            console.log('👤 Manual flow: Performing search, will fetch candidates');
            setManualSearchPerformed(true);
            // Clear any previously selected ambulance to show all options
            if (onAmbulanceSelect) {
              onAmbulanceSelect(null);
            }
            // Call candidates API
            if (onManualSearch) {
              onManualSearch();
            }
          } else if (allocationType === "Automatic") {
            // Automatic flow: Show confirmation modal for both fleet and hospital users
            // For fleet users, confirmation modal will trigger candidates API on "Yes"
            // For hospital users, confirmation modal will trigger existing hospital flow
            console.log('🔄 Automatic flow: Showing confirmation modal');
            setShowAutoAssignConfirmation(true);
          } else {
              console.log('⚠️ No allocation type selected:', allocationType);
            }
          }}
          disabled={
            !bookingFormData ||
            !allocationType ||
            // For fleet users, check all required fields based on allocation type
            (isFleetUser ? (
              allocationType === "Manual" ? (
                // Manual mode: check all required fields
                !bookingFormData.name?.trim() ||
                (!bookingFormData.phone?.trim() && !bookingFormData.email?.trim()) ||
                !pickupCoordinates ||
                !dropCoordinates ||
                (bookingFormData.bookingType === "scheduled" && (!bookingFormData.date || !bookingFormData.time))
              ) : (
                // Automatic mode: check all required fields including hospitalPartnerId
                !bookingFormData.name?.trim() ||
                (!bookingFormData.phone?.trim() && !bookingFormData.email?.trim()) ||
                !pickupCoordinates ||
                !dropCoordinates ||
                !bookingFormData.hospitalPartnerId ||
                (bookingFormData.bookingType === "scheduled" && (!bookingFormData.date || !bookingFormData.time))
              )
            ) : (
              // For hospital users, both automatic and manual allocation require partner selection
              // "all_partners" is valid - sends to all partners
              (selectedPartner === "all")
            ))
            // Removed ambulance type selection requirement - types are optional filters
          }
        >
          {allocationType === "Automatic" && (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Book Ambulance
            </>
          )}
          {allocationType === "Manual" && (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search Ambulance
            </>
          )}
          {!allocationType && (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Select Allocation Type First
            </>
          )}
        </button>
        )}
      </div>

      {/* Manual Booking Error Message - Only show when error exists */}
      {allocationType === "Manual" && manualBookingError && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex-shrink-0">
          <p className="text-sm text-red-700 font-medium">{manualBookingError}</p>
        </div>
      )}

      {/* Automatic Flow: Confirmation Modal before starting auto-assignment */}
      {showAutoAssignConfirmation && allocationType === "Automatic" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Start Auto-Assignment?</h3>
              <p className="text-gray-600 mb-6">
                Do you want to start the automatic ambulance assignment process? The system will automatically search and assign an available ambulance.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    console.log('✅ User confirmed auto-assignment');
                    setShowAutoAssignConfirmation(false);
                    
                    // For fleet users, trigger candidates API directly via handleFleetAutomaticBooking
                    if (isFleetUser) {
                      console.log('🚛 [Fleet Automatic] User confirmed - triggering candidates API');
                      handleFleetAutomaticBooking();
                      return;
                    }
                    
                    // For hospital users, continue with existing hospital flow
                    setAutoAssignInProgress(true);
                    setLoading(true);
                    setError("");

                    try {
                      // Validate required data
                      if (!bookingFormData) {
                        throw new Error('Booking form data is missing');
                      }
                      if (!pickupCoordinates || !dropCoordinates) {
                        throw new Error('Pickup and dropoff coordinates are required');
                      }

                      // Get hospital ID
                      const getHospitalId = () => {
                        if (typeof window !== 'undefined') {
                          try {
                            const hospitalIdKey = API_CONFIG.STORAGE_KEYS?.HOSPITAL_ID || 'hospitalId';
                            const hospitalIdFromKey = localStorage.getItem(hospitalIdKey);
                            if (hospitalIdFromKey) {
                              return String(hospitalIdFromKey);
                            }
                            
                            const hospitalDataKey = API_CONFIG.STORAGE_KEYS?.HOSPITAL_DATA || 'hospitalData';
                            const hospitalDataStr = localStorage.getItem(hospitalDataKey);
                            if (hospitalDataStr) {
                              try {
                                const hospitalData = JSON.parse(hospitalDataStr);
                                const hospitalIdFromData = hospitalData.hospital_id || hospitalData.id;
                                if (hospitalIdFromData) {
                                  return String(hospitalIdFromData);
                                }
                              } catch (e) {
                                // Failed to parse
                              }
                            }
                            
                            const userDataKey = API_CONFIG.STORAGE_KEYS?.USER_DATA || 'userData';
                            const userDataStr = localStorage.getItem(userDataKey);
                            if (userDataStr) {
                              try {
                                const parsedUser = JSON.parse(userDataStr);
                                const hospitalIdFromStorage =
                                  parsedUser.hospital_id ||
                                  parsedUser.hospitalId ||
                                  parsedUser.hospital?.hospital_id ||
                                  parsedUser.hospital?.id ||
                                  parsedUser.operator_id;
                                if (hospitalIdFromStorage) {
                                  return String(hospitalIdFromStorage);
                                }
                              } catch (e) {
                                // Failed to parse
                              }
                            }
                          } catch (error) {
                            console.error('Error getting hospital ID from localStorage:', error);
                          }
                        }
                        return user?.hospital_id || 
                               user?.hospitalId || 
                               user?.hospital?.hospital_id ||
                               user?.hospital?.id ||
                               null;
                      };

                      const hospitalId = getHospitalId();
                      if (!hospitalId) {
                        throw new Error('Hospital ID not found. Please ensure you are logged in as a hospital user.');
                      }

                      // Determine payment method
                      const paymentMethod = paymentType === "Online" ? "ONLINE" : 
                                           paymentType === "Cash" ? "CASH" : "PAY_LATER";

                      // Build filters object
                      const filters = {};

                      // Add operator_id filter if partner is selected (not "all" or "all_partners")
                      if (selectedPartner !== "all" && selectedPartner !== "all_partners") {
                        let operatorId = null;
                        if (typeof selectedPartner === 'string' && selectedPartner.includes('_')) {
                          operatorId = selectedPartner.split('_')[0];
                        } else {
                          operatorId = selectedPartner;
                        }
                        if (operatorId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(operatorId)) {
                          filters.operator_id = operatorId;
                        }
                      }

                      // Add distance range filter
                      if (kmRange && kmRange > 0) {
                        filters.distance_range_km = kmRange;
                      }

                      // Add ambulance type filter
                      if (selectedAmbulanceTypes.length > 0) {
                        filters.ambulance_type_ids = selectedAmbulanceTypes;
                      }

                      // Build booking payload
                      const bookingPayload = {
                        hospitalId: hospitalId,
                        patientName: bookingFormData.name || '',
                        patientPhone: bookingFormData.phone || '',
                        bookingDirection: journeyDirection || bookingFormData.direction || 'OUTBOUND',
                        pickupLocation: {
                          address: bookingFormData.pickupLocation || '',
                          latitude: typeof pickupCoordinates.latitude === 'number' 
                            ? pickupCoordinates.latitude 
                            : parseFloat(pickupCoordinates.latitude),
                          longitude: typeof pickupCoordinates.longitude === 'number'
                            ? pickupCoordinates.longitude
                            : parseFloat(pickupCoordinates.longitude)
                        },
                        dropoffLocation: {
                          address: bookingFormData.dropLocation || '',
                          latitude: typeof dropCoordinates.latitude === 'number'
                            ? dropCoordinates.latitude
                            : parseFloat(dropCoordinates.latitude),
                          longitude: typeof dropCoordinates.longitude === 'number'
                            ? dropCoordinates.longitude
                            : parseFloat(dropCoordinates.longitude)
                        },
                        paymentMethod: paymentMethod,
                        filters: Object.keys(filters).length > 0 ? filters : undefined
                      };

                      // Add online payment details if payment method is Online
                      if (paymentMethod === "ONLINE") {
                        // Get estimated fare from fare estimates or booking form data
                        const estimatedFare = fareEstimates && Object.keys(fareEstimates).length > 0
                          ? Object.values(fareEstimates)[0] // Use first available fare estimate
                          : null;
                        
                        bookingPayload.paymentDetails = {
                          paymentGateway: "razorpay", // Default payment gateway
                          currency: "INR", // Default currency
                          amount: estimatedFare || null, // Estimated fare amount
                          paymentMethod: "ONLINE",
                          // Include patient contact info for payment gateway prefill
                          patientPhone: bookingFormData.phone || '',
                          patientEmail: bookingFormData.email || null,
                          patientName: bookingFormData.name || ''
                        };
                        
                        console.log('💳 [Auto Allocation] Added online payment details to booking payload:', bookingPayload.paymentDetails);
                      }

                      console.log('🔄 [Auto Allocation] Step 1: Fetching available ambulances based on selected filters');

                      // STEP 1: First fetch available ambulances using candidates API
                      // This gets the full candidate list that will be used for auto-allocation
                      // Flow: getCandidates → startAutoAllocation (picks TOP 1) → triggerNextDriver (picks NEXT from same list)
                      // Build candidates request with filters
                      const candidatesData = {
                        hospitalId: hospitalId,
                        pickupLocation: {
                          address: bookingFormData.pickupLocation || "",
                          latitude: typeof pickupCoordinates.latitude === 'number' 
                            ? pickupCoordinates.latitude 
                            : parseFloat(pickupCoordinates.latitude),
                          longitude: typeof pickupCoordinates.longitude === 'number'
                            ? pickupCoordinates.longitude
                            : parseFloat(pickupCoordinates.longitude)
                        },
                        dropoffLocation: {
                          address: bookingFormData.dropLocation || '',
                          latitude: typeof dropCoordinates.latitude === 'number'
                            ? dropCoordinates.latitude
                            : parseFloat(dropCoordinates.latitude),
                          longitude: typeof dropCoordinates.longitude === 'number'
                            ? dropCoordinates.longitude
                            : parseFloat(dropCoordinates.longitude)
                        },
                        payment_method: paymentMethod
                      };

                      // Add filters to candidates request (not for "all" or "all_partners")
                      if (selectedPartner !== "all" && selectedPartner !== "all_partners") {
                        let operatorId = null;
                        if (typeof selectedPartner === 'string' && selectedPartner.includes('_')) {
                          operatorId = selectedPartner.split('_')[0];
                        } else {
                          operatorId = selectedPartner;
                        }
                        if (operatorId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(operatorId)) {
                          candidatesData.operator_id = operatorId;
                        }
                      }

                      if (kmRange && kmRange > 0) {
                        candidatesData.distance_range_km = kmRange;
                      }

                      if (selectedAmbulanceTypes.length > 0) {
                        candidatesData.ambulance_type_ids = selectedAmbulanceTypes;
                      }

                      console.log('🔍 [Auto Allocation] Fetching candidates with payload:', candidatesData);

                      // Call candidates API to get available ambulances - use fleet endpoint for fleet users
                      const candidatesResponse = isFleetUser 
                        ? await authService.fleetGetCandidates(candidatesData)
                        : await hospitalService.getCandidates(candidatesData);

                      if (!candidatesResponse.success || !candidatesResponse.data?.candidates) {
                        throw new Error(candidatesResponse.message || 'Failed to fetch available ambulances');
                      }

                      // Check if no candidates are available
                      if (candidatesResponse.data.candidates.length === 0) {
                        const noCandidatesMessage = 'No ambulances are currently available near this location. Please try increasing your search distance or choosing a different ambulance type.';
                        setError(noCandidatesMessage);
                        setLoading(false);
                        setAutoAssignInProgress(false);
                        
                        // Clear ambulance types in child component
                        setAmbulanceTypes([]);
                        setAllAmbulanceTypes([]);
                        
                        // IMPORTANT: Call onStartAutoAssignment FIRST to set autoAllocationConfirmed = true
                        // This ensures the black glass overlay UI is displayed even when candidates === 0
                        if (onStartAutoAssignment) {
                          console.log('📤 [Auto Allocation] Calling onStartAutoAssignment to show overlay UI (candidates === 0)');
                          onStartAutoAssignment(); // Call without bookingId since no allocation was created
                        }
                        
                        // Notify parent component to clear availableAmbulanceTypes AFTER confirmation is set
                        // Use setTimeout to ensure autoAllocationConfirmed state is set first
                        setTimeout(() => {
                          if (onAmbulanceTypesChange) {
                            console.log('📤 [Auto Allocation] Notifying parent of empty candidates list');
                            onAmbulanceTypesChange([]);
                          }
                        }, 50);
                        
                        console.warn('⚠️ [Auto Allocation] No candidates available:', noCandidatesMessage);
                        return; // Stop execution here
                      }

                      console.log('✅ [Auto Allocation] Available ambulances fetched:', candidatesResponse.data.candidates.length);

                      // Extract candidate IDs to pass to startAutoAllocation
                      // This ensures backend uses the EXACT same candidate list
                      const candidateVehicleIds = candidatesResponse.data.candidates
                        .map(c => c.vehicle_id)
                        .filter(id => id != null);
                      const candidateDriverIds = candidatesResponse.data.candidates
                        .map(c => c.driver?.id)
                        .filter(id => id != null);

                      console.log('📋 [Auto Allocation] Candidate list extracted:', {
                        totalCandidates: candidatesResponse.data.candidates.length,
                        vehicleIds: candidateVehicleIds.length,
                        driverIds: candidateDriverIds.length
                      });

                      // Transform candidates to ambulance format for display
                      const availableAmbulances = candidatesResponse.data.candidates.map(candidate => ({
                        vehicle_id: candidate.vehicle_id,
                        type_id: candidate.type?.id,
                        name: candidate.type?.name || 'Ambulance',
                        plate_number: candidate.plate_number,
                        driver: {
                          id: candidate.driver?.id,
                          name: candidate.driver?.name || '',
                          phone: candidate.driver?.phone || '',
                          rating: candidate.driver?.rating || null,
                          total_rides: candidate.driver?.total_rides
                        },
                        operator: {
                          id: candidate.operator?.id,
                          name: candidate.operator?.name,
                          is_partner: candidate.operator?.is_partner
                        },
                        operator_id: candidate.operator?.id,
                        eta_minutes: candidate.eta_minutes,
                        distanceFromPickup: candidate.distance_from_pickup_km || null,
                        estimated_fare: candidate.estimated_fare,
                        is_partner: candidate.operator?.is_partner || false,
                        operator_name: candidate.operator?.name,
                        // Add additional fields for compatibility
                        fleetId: candidate.operator?.id,
                        fleet_id: candidate.operator?.id,
                        fleetOperatorId: candidate.operator?.id,
                        pricing_source: candidate.pricing_source ?? null,
                        pricing_model: candidate.pricing_model ?? null,
                        pricing_breakdown: candidate.pricing_breakdown ?? candidate.breakdown ?? null
                      }));

                      // Update ambulance types with available ambulances
                      setAmbulanceTypes(availableAmbulances);
                      setAllAmbulanceTypes(availableAmbulances);

                      // Notify parent component to start auto-assignment FIRST (sets confirmed state)
                      // This must be called before onAmbulanceTypesChange to ensure state is set
                      if (onStartAutoAssignment) {
                        onStartAutoAssignment();
                      }

                      // Use setTimeout to ensure state update completes before sending types
                      // This prevents race condition where types arrive before confirmed state is set
                      setTimeout(() => {
                        // Notify parent component of the available ambulances (real-time from API)
                        if (onAmbulanceTypesChange) {
                          console.log("📤 [ChooseAmbulance] Sending available ambulances to parent after confirmation");
                          onAmbulanceTypesChange(availableAmbulances);
                        }
                      }, 50);

                      console.log('✅ [Auto Allocation] Step 1 complete: Available ambulances displayed in UI');
                      
                      // STEP 2: Start auto-allocation API to get booking_id for polling
                      // Pass candidate IDs so backend picks TOP 1 from this specific candidate list
                      // The backend will store this candidate list and use it for triggerNextDriver calls
                      console.log('🔄 [Auto Allocation] Step 2: Starting auto-allocation API to create booking');
                      console.log('🔄 [Auto Allocation] Passing candidate list to backend for top 1 selection');
                      console.log('🔄 [Auto Allocation] Backend will use this exact candidate list for all subsequent triggerNextDriver calls');
                      
                      // Add candidate IDs to bookingPayload so backend uses this exact list
                      const bookingPayloadWithCandidates = {
                        ...bookingPayload,
                        // Pass candidate vehicle_ids and driver_ids so backend uses this exact list
                        candidate_vehicle_ids: candidateVehicleIds.length > 0 ? candidateVehicleIds : undefined,
                        candidate_driver_ids: candidateDriverIds.length > 0 ? candidateDriverIds : undefined,
                        // Also pass the full candidate list count for reference
                        total_candidates: candidatesResponse.data.candidates.length
                      };
                      
                      // Use fleet endpoint for fleet users, hospital endpoint for hospital users
                      const autoAllocationResult = isFleetUser
                        ? await authService.fleetStartAutoAllocation(bookingPayloadWithCandidates)
                        : await hospitalService.startAutoAllocation(bookingPayloadWithCandidates);
                      
                      if (!autoAllocationResult.success || !autoAllocationResult.data) {
                        throw new Error(autoAllocationResult.message || 'Failed to start auto allocation');
                      }
                      
                      const bookingId = autoAllocationResult.data.booking_id;
                      
                      if (!bookingId) {
                        throw new Error('No booking ID received from auto allocation API');
                      }
                      
                      console.log('✅ [Auto Allocation] Auto-allocation started successfully:', {
                        booking_id: bookingId,
                        response_id: autoAllocationResult.data.response_id,
                        status: autoAllocationResult.data.status,
                        expires_in_sec: autoAllocationResult.data.expires_in_sec,
                        driver: autoAllocationResult.data.driver,
                        vehicle: autoAllocationResult.data.vehicle,
                        estimated_fare: autoAllocationResult.data.estimated_fare
                      });
                      
                      // Pass booking_id and initial allocation data to parent component via callback
                      if (onStartAutoAssignment) {
                        // Pass bookingId and initial allocation data as parameter
                        if (typeof onStartAutoAssignment === 'function') {
                          // Pass the full allocation result data so parent can use it immediately
                          onStartAutoAssignment(bookingId, autoAllocationResult.data);
                        } else {
                          onStartAutoAssignment();
                        }
                      }
                      
                      console.log('✅ [Auto Allocation] Step 2 complete: Booking created with ID:', bookingId);

                    } catch (error) {
                      console.error('❌ [Auto Allocation] Error starting auto allocation:', error);
                      const errorMessage = error?.response?.data?.message || 
                                         error?.response?.data?.error || 
                                         error?.message || 
                                         'Failed to start auto allocation';
                      setError(errorMessage);
                      setAutoAssignInProgress(false);
                      // Show error to user
                      alert(`Error: ${errorMessage}`);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? 'Starting...' : 'Yes'}
                </button>
                <button
                  onClick={() => {
                    console.log('❌ User cancelled auto-assignment');
                    setShowAutoAssignConfirmation(false);
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-6 rounded-lg font-semibold transition-colors"
                >
                  No
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Allocation Modal */}
      <ManualAllocationModal
        isOpen={isManualAllocationModalOpen}
        onClose={() => {
          console.log('❌ [Manual Allocation] Cancel clicked - stopping allocation process');
          setIsManualAllocationModalOpen(false);
          // Clear selected ambulance to allow user to choose a different one
          if (onAmbulanceSelect) {
            onAmbulanceSelect(null);
          }
          // Notify parent to stop polling and reset allocation state
          // This will be handled by the parent's onClose callback if provided
        }}
        bookingData={bookingFormData}
        selectedAmbulance={selectedAmbulance}
        ambulances={ambulanceTypes}
        drivers={availableDrivers}
        allocationStatus={allocationStatus}
        bookingId={bookingId}
        isPollingAllocation={isPollingAllocation}
        onStopAllocation={onStopAllocation}
        onRefetchAllocationStatus={onRefetchAllocationStatus}
        onRefetchCandidates={async (bookingIdToRefetch) => {
          // Handler to refetch candidates API with bookingId (for timeout scenario or after stop allocation)
          console.log('🔄 [ChooseAmbulance] Refetching candidates for booking:', bookingIdToRefetch);
          console.log('🔄 [ChooseAmbulance] Current state:', {
            bookingId: bookingIdToRefetch,
            hasPickupCoordinates: !!pickupCoordinates,
            hasDropCoordinates: !!dropCoordinates,
            hasBookingFormData: !!bookingFormData,
            selectedPartner,
            kmRange,
            selectedAmbulanceTypes,
            isFleetUser: isFleetUser,
            hasParentRefetch: !!parentRefetchFn
          });
          
          if (!bookingIdToRefetch) {
            console.error('❌ [ChooseAmbulance] Missing bookingId');
            return;
          }

          if (!pickupCoordinates || !dropCoordinates || !bookingFormData) {
            console.warn('⚠️ [ChooseAmbulance] Missing required data for candidates refetch:', {
              pickupCoordinates: !!pickupCoordinates,
              dropCoordinates: !!dropCoordinates,
              bookingFormData: !!bookingFormData
            });
            return;
          }

          // For fleet users, use parent's refetch function if provided
          if (isFleetUser && parentRefetchFn) {
            console.log('🚛 [ChooseAmbulance] Fleet user - using parent refetch function');
            setLoading(true);
            setError("");
            try {
              await parentRefetchFn(bookingIdToRefetch);
              console.log('✅ [ChooseAmbulance] Parent refetch completed');
              setLoading(false);
              return;
            } catch (error) {
              console.error('❌ [ChooseAmbulance] Error in parent refetch:', error);
              setError('Failed to refetch candidates. Please try again.');
              setLoading(false);
              return;
            }
          }

          // For hospital users or if parent refetch not available, use local implementation
          setLoading(true);
          setError("");

          try {
            // Determine payment method
            const paymentMethod = bookingFormData.paymentType === "Online" ? "ONLINE" : 
                                 bookingFormData.paymentType === "Cash" ? "CASH" : "PAY_LATER";

            // Hospital user flow - use existing hospital API
              // Get hospital ID - use comprehensive logic matching parent component
              const getHospitalIdFromUser = () => {
                // PRIORITY 1: Check localStorage with proper keys
                if (typeof window !== 'undefined') {
                  try {
                    const hospitalIdKey = API_CONFIG.STORAGE_KEYS?.HOSPITAL_ID || 'hospitalId';
                    const hospitalIdFromKey = localStorage.getItem(hospitalIdKey);
                    
                    if (hospitalIdFromKey) {
                      return String(hospitalIdFromKey);
                    }
                    
                    // Check hospital data
                    const hospitalDataKey = API_CONFIG.STORAGE_KEYS?.HOSPITAL_DATA || 'hospitalData';
                    const hospitalDataStr = localStorage.getItem(hospitalDataKey);
                    if (hospitalDataStr) {
                      try {
                        const hospitalData = JSON.parse(hospitalDataStr);
                        const hospitalIdFromData = hospitalData.hospital_id || hospitalData.id;
                        if (hospitalIdFromData) {
                          localStorage.setItem(hospitalIdKey, String(hospitalIdFromData));
                          return String(hospitalIdFromData);
                        }
                      } catch (e) {
                        // Failed to parse hospital data
                      }
                    }
                    
                    // Check user data
                    const userDataKey = API_CONFIG.STORAGE_KEYS?.USER_DATA || 'userData';
                    const userDataStr = localStorage.getItem(userDataKey);
                    if (userDataStr) {
                      try {
                        const parsedUser = JSON.parse(userDataStr);
                        const hospitalIdFromStorage =
                          parsedUser.hospital_id ||
                          parsedUser.hospitalId ||
                          parsedUser.hospital?.hospital_id ||
                          parsedUser.hospital?.id ||
                          parsedUser.operator_id;
                        
                        if (hospitalIdFromStorage) {
                          localStorage.setItem(hospitalIdKey, String(hospitalIdFromStorage));
                          return String(hospitalIdFromStorage);
                        }
                      } catch (e) {
                        // Failed to parse user data
                      }
                    }
                    
                    // Try alternative keys
                    const alternativeKeys = ['user', 'userData', 'auth_user', 'currentUser'];
                    for (const key of alternativeKeys) {
                      const altData = localStorage.getItem(key);
                      if (altData) {
                        try {
                          const parsed = JSON.parse(altData);
                          const altHospitalId = parsed.hospital_id || parsed.hospitalId || parsed.hospital?.hospital_id;
                          if (altHospitalId) {
                            localStorage.setItem(hospitalIdKey, String(altHospitalId));
                            return String(altHospitalId);
                          }
                        } catch (e) {
                          // Failed to parse data from key
                        }
                      }
                    }
                  } catch (error) {
                    console.error('Error getting hospital ID from localStorage:', error);
                  }
                }
                
                // PRIORITY 2: Fallback to user object from auth context
                return user?.hospital_id || 
                       user?.hospitalId || 
                       user?.hospital?.hospital_id ||
                       user?.hospital?.id ||
                       user?.hospitals?.[0]?.hospital_id ||
                       null;
              };

              const hospitalId = getHospitalIdFromUser();
              if (!hospitalId) {
                console.error('❌ [ChooseAmbulance] Hospital ID not found. Available sources:', {
                  userHospitalId: user?.hospital_id,
                  userHospitalIdCamel: user?.hospitalId,
                  userHospital: user?.hospital,
                  localStorageKeys: typeof window !== 'undefined' ? {
                    hospitalId: localStorage.getItem(API_CONFIG.STORAGE_KEYS?.HOSPITAL_ID || 'hospitalId'),
                    hospitalData: localStorage.getItem(API_CONFIG.STORAGE_KEYS?.HOSPITAL_DATA || 'hospitalData'),
                    userData: localStorage.getItem(API_CONFIG.STORAGE_KEYS?.USER_DATA || 'userData')
                  } : 'N/A (server-side)'
                });
                throw new Error('Hospital ID not found. Please ensure you are logged in as a hospital user.');
              }
              
              console.log('✅ [ChooseAmbulance] Hospital ID retrieved:', hospitalId);

              // Prepare candidates request with bookingId - match exact API structure
              // This bookingId comes from manual-allocation/start response
              // The backend will use it to exclude the assigned driver who declined or timed out
              const candidatesData = {
                bookingId: String(bookingIdToRefetch), // BookingId from manual-allocation/start - excludes assigned driver
                pickupLocation: {
                  address: bookingFormData.pickupLocation || "",
                  latitude: typeof pickupCoordinates.latitude === 'number' 
                    ? pickupCoordinates.latitude 
                    : parseFloat(pickupCoordinates.latitude),
                  longitude: typeof pickupCoordinates.longitude === 'number'
                    ? pickupCoordinates.longitude
                    : parseFloat(pickupCoordinates.longitude)
                },
                dropoffLocation: {
                  address: bookingFormData.dropLocation || "",
                  latitude: typeof dropCoordinates.latitude === 'number'
                    ? dropCoordinates.latitude
                    : parseFloat(dropCoordinates.latitude),
                  longitude: typeof dropCoordinates.longitude === 'number'
                    ? dropCoordinates.longitude
                    : parseFloat(dropCoordinates.longitude)
                },
                payment_method: paymentMethod
              };
              
              console.log('📋 [ChooseAmbulance] Candidates API payload includes bookingId:', candidatesData.bookingId);
              console.log('📋 [ChooseAmbulance] This bookingId will exclude the assigned driver who declined/timed out');

              // Add filters if applicable (not for "all" or "all_partners")
              if (selectedPartner !== "all" && selectedPartner !== "all_partners") {
                // Extract operator_id from selectedPartner (format: "operatorId_typeId")
                let operatorId = null;
                if (typeof selectedPartner === 'string' && selectedPartner.includes('_')) {
                  operatorId = selectedPartner.split('_')[0];
                } else if (typeof selectedPartner === 'string') {
                  operatorId = selectedPartner;
                }

                if (operatorId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(operatorId)) {
                  candidatesData.operator_id = operatorId;
                }
              }

              if (kmRange && kmRange < 50) {
                candidatesData.distance_range_km = kmRange;
              }

              if (selectedAmbulanceTypes.length > 0) {
                candidatesData.ambulance_type_ids = selectedAmbulanceTypes;
              }

              console.log('🔍 [ChooseAmbulance] Refetching candidates with payload:', JSON.stringify(candidatesData, null, 2));
              console.log('🔍 [ChooseAmbulance] About to call candidates API...');

              // Make the API call - use fleet endpoint for fleet users
              const response = isFleetUser
                ? await authService.fleetGetCandidates(candidatesData)
                : await hospitalService.getCandidates(candidatesData);
            console.log('📥 [ChooseAmbulance] API call completed, response received:', {
              success: response?.success,
              hasData: !!response?.data,
              hasCandidates: !!response?.data?.candidates,
              candidatesCount: response?.data?.candidates?.length || 0
            });
            
            console.log('📥 [ChooseAmbulance] API Response:', {
              success: response?.success,
              hasData: !!response?.data,
              hasCandidates: !!response?.data?.candidates,
              candidatesCount: response?.data?.candidates?.length || 0,
              response: response
            });
            
            if (response && response.success && response.data?.candidates) {
              console.log('✅ [ChooseAmbulance] Candidates refetched successfully:', response.data.candidates.length);
              
              // Check if no candidates are available
              if (response.data.candidates.length === 0) {
                const noCandidatesMessage = 'No ambulances are currently available near this location. Please try increasing your search distance or choosing a different ambulance type.';
                setError(noCandidatesMessage);
                console.warn('⚠️ [ChooseAmbulance] No candidates available:', noCandidatesMessage);
                setAllAmbulanceTypes([]); // Clear ambulance types
                if (onAmbulanceTypesChange) {
                  onAmbulanceTypesChange([]); // Notify parent of empty list
                }
                setLoading(false);
                return; // Stop execution here
              }
              
              // Transform candidates to match ambulance UI format
              const transformedCandidates = response.data.candidates.map(candidate => ({
                vehicle_id: candidate.vehicle_id,
                type_id: candidate.type?.id,
                name: candidate.type?.name,
                plate_number: candidate.plate_number,
                driver: {
                  id: candidate.driver?.id,
                  name: candidate.driver?.name,
                  phone: candidate.driver?.phone,
                  rating: candidate.driver?.rating,
                  total_rides: candidate.driver?.total_rides
                },
                operator: {
                  id: candidate.operator?.id,
                  name: candidate.operator?.name,
                  is_partner: candidate.operator?.is_partner
                },
                operator_id: candidate.operator?.id, // Add operator_id at top level for filtering
                eta_minutes: candidate.eta_minutes,
                distanceFromPickup: candidate.distance_from_pickup_km || null, // Use distance_from_pickup_km from API
                distance_from_pickup_km: candidate.distance_from_pickup_km, // Keep original field name
                estimated_fare: candidate.estimated_fare,
                is_partner: candidate.operator?.is_partner || false,
                operator_name: candidate.operator?.name,
                // Add additional fields that filtering might check
                fleetId: candidate.operator?.id,
                fleet_id: candidate.operator?.id,
                fleetOperatorId: candidate.operator?.id,
                partnerId: candidate.operator?.id,
                partner_id: candidate.operator?.id
              }));

              console.log('🔄 [ChooseAmbulance] Transformed candidates:', transformedCandidates.length);

              // Update state directly to ensure UI updates
              console.log('🔄 [ChooseAmbulance] Setting allAmbulanceTypes with', transformedCandidates.length, 'candidates');
              setAllAmbulanceTypes(transformedCandidates);
              
              // Also update via callback to notify parent
              if (onAmbulanceTypesChange) {
                console.log('📤 [ChooseAmbulance] Notifying parent of new candidates');
                onAmbulanceTypesChange(transformedCandidates);
              }

              // Clear selection to show new candidates
              if (onAmbulanceSelect) {
                console.log('🔄 [ChooseAmbulance] Clearing selected ambulance');
                onAmbulanceSelect(null);
              }

              // Note: Modal will be closed by the button handler after this callback completes
              console.log('✅ [ChooseAmbulance] Candidates refetch completed successfully');
            } else {
              const errorMsg = response?.message || 'No candidates in response';
              console.warn('⚠️ [ChooseAmbulance]', errorMsg, response);
              setError(errorMsg);
            }
          } catch (error) {
            const errorMessage = error?.response?.data?.message || 
                               error?.response?.data?.error || 
                               error?.message || 
                               'Failed to refetch candidates';
            console.error('❌ [ChooseAmbulance] Error refetching candidates:', error);
            console.error('❌ [ChooseAmbulance] Error details:', {
              message: errorMessage,
              response: error?.response,
              stack: error?.stack
            });
            setError(errorMessage);
          } finally {
            setLoading(false);
          }
        }}
        onSelect={(selection) => {
          console.log('✅ Manual allocation completed:', selection);
          setIsManualAllocationModalOpen(false);
          
          // Update bookingFormData with manual allocation data
          const updatedFormData = {
            ...bookingFormData,
            paymentType: paymentType,
            allocationType: allocationType,
            manualAllocation: {
              ambulance: selection.ambulance,
              driver: selection.driver,
            },
          };
          
          // Call onConfirmSelection with updated form data including manual allocation
          if (onConfirmSelection) {
            onConfirmSelection(selectedAmbulance, updatedFormData);
          }
        }}
      />

      {/* Automatic Allocation Modal */}
      <AutomaticAllocationModal
        isOpen={isAutomaticAllocationModalOpen}
        onClose={() => setIsAutomaticAllocationModalOpen(false)}
        bookingData={bookingFormData}
        selectedAmbulance={selectedAmbulance}
        availableDrivers={availableDrivers}
        onAllocationComplete={(allocationResult) => {
          console.log('✅ Automatic allocation completed:', allocationResult);
          setIsAutomaticAllocationModalOpen(false);
          
          // Update bookingFormData with automatic allocation data
          const updatedFormData = {
            ...bookingFormData,
            paymentType: paymentType,
            allocationType: allocationType,
            automaticAllocation: {
              driver: allocationResult.driver,
              ambulance: allocationResult.ambulance,
            },
          };
          
          // Call onConfirmSelection with updated form data including automatic allocation
          if (onConfirmSelection) {
            onConfirmSelection(selectedAmbulance, updatedFormData);
          }
        }}
        onAllocationFailed={(failureResult) => {
          console.log('❌ Automatic allocation failed:', failureResult);
          setIsAutomaticAllocationModalOpen(false);
          // Could show an error message or fallback to manual allocation
        }}
      />

      {/* Error Modal for Automatic Booking */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => {
          setErrorModal({
            isOpen: false,
            title: "Validation Error",
            message: "",
          });
        }}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>

    {/* Pricing breakdown overlay */}
    {pricingInfoData && (() => {
      const pb = pricingInfoData.pricing_breakdown || {};
      const num = (v) => (v != null && v !== '' && typeof v === 'number' && !Number.isNaN(v) ? `₹${v}` : '—');
      const formatAdjustment = (adj) => {
        if (adj == null || adj === '') return '—';
        if (typeof adj === 'object') {
          const amountNum = Number(adj.amount);
          const amt = !Number.isNaN(amountNum) ? `₹${amountNum}` : null;
          const type = adj.type ?? '';
          const pct = adj.percent != null && adj.percent !== '' ? `${adj.percent}%` : '';
          const parts = [type, pct].filter(Boolean).join(' ');
          if (amt) return parts ? `${parts} • ${amt}` : amt;
          return parts || '—';
        }
        if (typeof adj === 'number' && !Number.isNaN(adj)) return `₹${adj}`;
        return '—';
      };
      const toNum = (v) => (v != null && v !== '' && typeof v === 'number' && !Number.isNaN(v) ? v : 0);
      const adjustmentSigned = (adj) => {
        if (adj == null || adj === '') return 0;
        const amt = typeof adj === 'object' && adj.amount != null ? toNum(adj.amount) : (typeof adj === 'number' ? adj : 0);
        if (amt === 0) return 0;
        const type = (adj && typeof adj === 'object' && (adj.type ?? adj.adjustmentType ?? '')).toString().toUpperCase();
        return type === 'DISCOUNT' ? -amt : amt;
      };
      const parts = [
        toNum(pb.base_fare ?? pb.baseFare),
        toNum(pb.distance_fare ?? pb.distanceFare),
        toNum(pb.time_fare ?? pb.timeFare),
        toNum(pb.driver_charges ?? pb.driverCharges),
        toNum(pb.taxes),
        toNum(pb.commission),
        adjustmentSigned(pb.adjustment),
      ];
      const sum = parts.reduce((a, b) => a + b, 0);
      const decimal = sum % 1;
      const total = decimal <= 0.4 ? Math.floor(sum) : Math.ceil(sum);
      const nonZeroParts = parts.filter((v) => v !== 0);
      const totalFormula = nonZeroParts.reduce((acc, v, idx) => {
        const abs = Math.abs(v);
        if (idx === 0) return `${abs}`;
        return v < 0 ? `${acc} - ${abs}` : `${acc} + ${abs}`;
      }, '');
      return createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
          onClick={() => setPricingInfoData(null)}
          role="dialog"
          aria-label="Pricing breakdown"
        >
          <div
            className="w-full max-w-sm rounded-xl border border-gray-500 bg-gray-900 shadow-2xl p-4 text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-semibold text-white mb-3 border-b border-gray-600 pb-2">Pricing breakdown</div>
            <div className="space-y-2 text-sm text-gray-300">
              <div className="flex justify-between gap-4"><span className="text-gray-400">Source</span><span className="font-medium text-gray-200">{pricingInfoData.pricing_source ?? '—'}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Model</span><span className="font-medium text-gray-200">{pricingInfoData.pricing_model ?? '—'}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Base fare</span><span>{num(pb.base_fare ?? pb.baseFare)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Distance fare</span><span>{num(pb.distance_fare ?? pb.distanceFare)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Time fare</span><span>{num(pb.time_fare ?? pb.timeFare)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Driver charges</span><span>{num(pb.driver_charges ?? pb.driverCharges)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Taxes</span><span>{num(pb.taxes)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Commission</span><span>{num(pb.commission)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-400">Adjustment</span><span>{formatAdjustment(pb.adjustment)}</span></div>
              <div className="flex justify-between gap-4 pt-2 mt-2 border-t border-gray-600">
                <span className="text-gray-300 font-medium">Total{totalFormula ? ` (${totalFormula})` : ''}</span>
                <span className="font-semibold text-white">₹{total}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPricingInfoData(null)}
              className="mt-3 w-full py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      );
    })()}
    </>
  );
};

// Ensure component is properly exported
export default ChooseAmbulanceDuplicate;

